/**
 * Workspace types for multi-workspace-type support.
 * Uses discriminated unions to type-safely handle different workspace types.
 */

import type { QueryResultSet, QueryExecutionStats, ResultCacheCapabilities } from '../../../../types/queryExecution';
import type { ExecutionPlanArtifact } from '../../../../types/executionPlan';
import {
  QUERY_STORE_DEFAULT_LIMIT,
  QUERY_STORE_DEFAULT_MIN_EXECUTIONS,
  QUERY_STORE_DEFAULT_PERIOD_HOURS,
  type QueryStoreMetric,
} from '../../../../types/queryStore';
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { EditorWordWrap } from '../../../../store/appStore';
import { getSchemaCompareDefaultOptions } from '../../../../store/appStore';
import { getStandardIgnoredProperties } from '../../schema-compare/utils/compareDepth';
import type { NotebookTabContent } from '../../notebook/types/notebookTypes';
import type { DesignerColumnRename } from '../../table-designer/types/designerSave';
import type { ReferencesTabContent } from '../../../navigational-features/find-references/referencesTabTypes';
import type { JsonClassification } from '../../../sql-resolver/types/schema';
import type { UserFacingEngineId } from '../../../database-blueprint/types/databaseBlueprintTypes';
import type { SpatialViewDefinition } from '../../spatial/types/spatialTypes';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { StorageResourceRef } from '../../../../types/azureStorage';
// Type-only — erased at compile, so this doesn't create a runtime cycle even
// though `filterUtils.ts` imports FROM this file.
import type { DataTypeCategory } from '../../common/utils/filterUtils';
// Data Import Wizard contracts — imported from the ambient `electron.d.ts` re-export
// (NOT the electron/ path directly): a direct import from a regular .ts file crosses
// tsconfig.node.json's composite project-reference boundary and hits TS6305 ("has not
// been built from source file") when the electron project isn't pre-built. See the
// re-export comment in `src/types/electron.d.ts` for the full explanation.
import type {
  ImportParseOptions,
  ImportRowFilter,
  ImportTargetMapping,
  ImportTargetPlan,
} from '../../../../types/electron';

// Re-export for convenience
export type { QueryResultSet, QueryExecutionStats };

// ============================================================================
// Transaction Types
// ============================================================================

/**
 * Transaction commit mode for query tabs.
 * - 'auto': Each query auto-commits (default, current behavior)
 * - 'manual': User must explicitly commit/rollback
 * - 'smart': Auto-detect DML and switch to manual; SELECTs auto-commit
 */
export type TransactionMode = 'auto' | 'manual' | 'smart';

/**
 * Transaction state within a query tab.
 * - 'idle': No active transaction
 * - 'active': Transaction is in progress
 * - 'error': Transaction encountered an error (e.g., connection drop)
 */
export type TransactionState = 'idle' | 'active' | 'error';

/**
 * Transaction isolation level.
 * - 'default': Use engine default (most common)
 * - Other values map to standard SQL isolation levels
 */
export type TransactionIsolationLevel =
  | 'read-uncommitted'
  | 'read-committed'
  | 'repeatable-read'
  | 'serializable'
  | 'default';

// ============================================================================
// Editor Options (per-tab settings)
// ============================================================================

/**
 * Result display mode for query results.
 * - 'grid': Default tabular grid view (TanStack Table)
 * - 'plaintext': ASCII-art text table (like SSMS Ctrl+T)
 */
export type ResultDisplayMode = 'grid' | 'plaintext';

/**
 * Per-tab connection-failure banner state (Slice 6 — Connection Diagnostics).
 *
 * Set when an execution against the tab's scope database fails with a
 * structured `isDatabaseError` (pool-open or DB-scoped failure). The banner
 * is rendered above the tab content with a *Show details* affordance that
 * opens the diagnostics dialog scoped to `scopeDatabase`. Carries
 * `dismissedAt` so users can hide it once acknowledged; refilled on the
 * next failed execution.
 */
export type ConnectionFailureBannerState = {
  /** SQL Server-style numeric error code (e.g. 4060, 916), if present. */
  sqlErrorCode?: number;
  /** Compact message — already user-friendly (e.g. "Cannot open database 'X'"). */
  message: string;
  /** Database the failure was scoped to. */
  scopeDatabase?: string;
  /** Set when the user clicks the dismiss button — banner stops rendering. */
  dismissedAt?: number;
};

/**
 * Per-tab editor options. When a tab is created, these are initialized from
 * the global defaults in appStore.editorSettings.
 */
export type TabEditorOptions = {
  wordWrap: EditorWordWrap;
  formatOnExecute: boolean;
  /** Result display mode (defaults to 'grid' if not set) */
  resultDisplayMode?: ResultDisplayMode;
};

// ============================================================================
// Workspace Type
// ============================================================================

/**
 * All supported workspace types.
 * Add new workspace types here as they are implemented.
 */
export type WorkspaceType = 'query' | 'table-editor' | 'table-explorer' | 'plan' | 'table-designer' | 'security' | 'schema-compare' | 'data-compare' | 'dependency-viewer' | 'schema-overview' | 'data-profile' | 'start-page' | 'notebook' | 'session-browser' | 'tablespace-manager' | 'oracle-object-detail' | 'data-pump' | 'instance-monitor' | 'filegroup-manager' | 'pl-sql-debugger' | 'job-scheduler' | 'plsql-editor' | 'package-explorer' | 'references-tab' | 'database-blueprint' | 'cross-engine-migrate' | 'dashboard' | 'spatial-explorer' | 'data-import' | 'deadlock-analyzer' | 'query-store' | 'storage-explorer';

// ============================================================================
// Query Message Type (used across workspaces)
// ============================================================================

/**
 * One-click next step rendered as an inline button on a message row
 * (Query Editor only — the panel renders it only when a handler is wired).
 * Kinds are a closed set so the handler can switch exhaustively.
 */
export type QueryMessageAction = {
  kind: 'switch_database' | 'open_session_browser';
  label: string;
};

export type QueryMessage = {
  id: string;
  type: 'info' | 'error' | 'success' | 'warning';
  content: string;
  timestamp: Date;
  /** Optional one-click next step (e.g. "Switch database…" under an object-not-found error). */
  action?: QueryMessageAction;
};

// ============================================================================
// Tab Content Types (discriminated by workspace type)
// ============================================================================

/**
 * A pinned execution snapshot. One pin click captures the entire execution:
 * every result set plus the output messages (PRINT/RAISERROR), so the pinned
 * tab can render the same Output / Result 1 / Result 2 sub-tabs as the
 * original execution.
 */
export type PinnedResultEntry = {
  id: string;
  /** All result sets from the pinned execution (parallel to sourceUris) */
  resultSets: QueryResultSet[];
  /** Output messages captured at pin time (drives the inner Output tab) */
  messages?: QueryMessage[];
  pinnedAt: string;
  query: string;
  /** Per-result-set source URI for windowed pinned results; undefined for inline result sets */
  sourceUris?: (string | undefined)[];
  /** Per-result-set total row counts for windowed pinned results */
  sourceRowCounts?: (number | undefined)[];
  /** Per-result-set capabilities, frozen at pin time */
  capabilitiesByIndex?: (ResultCacheCapabilities | undefined)[];
};

/** How a Query Editor tab shows its SQL: the text, or the Visual Query Editor's cards. */
export type QueryViewMode = 'sql' | 'visual';

/**
 * Content for query editor tabs
 */
export type QueryTabContent = {
  /** SQL query text */
  query: string;
  /**
   * `'visual'` shows the statement at `visualStatementOffset` as Visual Query
   * Editor cards over the same buffer (beta `visual-query-editor`; ignored when
   * the beta is off). Absent = SQL.
   */
  viewMode?: QueryViewMode;
  /** Start offset (in `query`) of the statement the Visual view shows. */
  visualStatementOffset?: number | null;
  /** Optional file path when this query tab is tied to a saved/opened `.sql` file */
  filePath?: string;
  /** Hash of the last saved/loaded file contents for dirty tracking */
  fileLastSavedHash?: string;
  /** When true, Run captures an actual execution plan (engine-dependent). */
  includeActualPlan?: boolean;
  /** Result sets from query execution (engine-neutral format) */
  resultSets: QueryResultSet[];
  /**
   * Messages from query execution. ACCUMULATES across runs in grid mode (the
   * Messages tab is a log), so it holds every run the tab has done.
   */
  messages: QueryMessage[];
  /**
   * Index in `messages` where the LATEST run's own messages begin — everything
   * before it belongs to earlier runs.
   *
   * The Output view and the result-set strip's Output tab describe ONE
   * execution: they place `[Result N]` markers between the lines a script
   * printed, so feeding them the whole accumulated log repeats every earlier
   * run's PRINTs and spends the markers on the first run's row counts. The
   * Messages tab still shows everything; only the Output surfaces slice from
   * here. Absent on a tab written before this field existed (and on plaintext
   * mode, which resets the log each run) — treat `undefined` as 0.
   */
  messagesRunStart?: number;
  /**
   * Index in `messages` just past the latest run's own messages, when
   * something has appended to the log SINCE that run without being part of it
   * — an estimated-plan capture is the case that exists. Absent means "to the
   * end", which is what an ordinary run leaves.
   */
  messagesRunEnd?: number;
  /** Execution statistics */
  stats?: QueryExecutionStats;
  /** Latest captured execution plan (raw payload stored in main process) */
  executionPlan?: ExecutionPlanArtifact;
  /** True when a script is being generated (metadata being fetched) */
  isGeneratingScript?: boolean;
  /**
   * VS Code snippet to insert on mount (with ${N:placeholder} tabstops).
   * Used by "Script New..." context menu actions.
   * Cleared after insertion.
   */
  insertSnippetOnMount?: string;
  /**
   * File to re-read on mount because the persisted snapshot omitted the tab's
   * script (over `PERSISTED_SQL_LIMIT_CHARS`, see editorTextLimits.ts). The
   * tab mounts detached (no `filePath`) with a placeholder body; a successful
   * reload restores the script and re-attaches the file. Cleared once handled.
   */
  pendingFileReload?: string;
  /**
   * When true, the QueryEditorTab runs the tab's `query` via the normal
   * execute flow on first mount, then clears the flag. Used by the
   * "Select top 1000 rows" context menu so the new tab goes through the
   * same code path as the Execute button (loading state, history,
   * analytics, transactions). Cleared before execution to prevent
   * re-triggering on re-render or session restore.
   */
  autoExecuteOnMount?: boolean;
  /** Chart configurations keyed by result set index */
  chartConfigs?: Record<number, ChartConfig>;
  /**
   * Per-tab editor options (word wrap, format on execute).
   * Initialized from global defaults when tab is created.
   */
  editorOptions?: TabEditorOptions;
  /** AI Chat session ID for resuming conversations (persisted) */
  aiChatSessionId?: string;
  /** AI Chat sidebar open state (persisted) */
  aiChatSidebarOpen?: boolean;
  /** Whether column profiling badges are shown in the results grid */
  showColumnProfiling?: boolean;
  /** Whether the result filter row is shown in the results grid */
  showResultFilter?: boolean;
  /** Pinned result set snapshots that survive query re-execution (multi-pin) */
  pinnedResultSets?: Array<PinnedResultEntry>;
  /** Per-result-set cache URIs in Electron main process (for windowed row access). Index matches resultSets index. Slots may be `undefined` for DML/DDL result sets that have no cache. */
  resultSetCacheUris?: (string | undefined)[];
  /** Monotonically increasing counter, bumped on each query execution, so windowed buffer resets on re-execution */
  cacheGeneration?: number;
  /** Transaction commit mode (default: 'auto') */
  transactionMode: TransactionMode;
  /** Current transaction state (default: 'idle') */
  transactionState: TransactionState;
  /**
   * Identity of the backend session this tab's transaction runs on
   * (`transaction:begin` → `sessionGeneration`). Sent back with every statement
   * and Save; a `transaction:session-released` event resets the tab only while
   * it still holds the generation the event names.
   */
  transactionSessionGeneration?: number;
  /**
   * Set while this tab's BEGIN is on the wire (`beginTabTransaction`): the tab
   * is preparing a transaction, so closing it and changing its mode are held
   * off like with an open one. Runtime-only — never persisted or restored.
   */
  transactionBeginToken?: string;
  /** Transaction isolation level (default: 'default') */
  isolationLevel: TransactionIsolationLevel;
  /**
   * @deprecated Legacy field for backward compatibility
   */
  results?: unknown[];
  /**
   * @deprecated Legacy field for backward compatibility
   */
  columns?: { id: string; name: string }[];
  /** Slice 6 — surfaces a dismissable banner when an execution fails against the tab's scope DB. */
  connectionFailure?: ConnectionFailureBannerState;
};

export type ExecutionPlanWorkspaceView = 'single' | 'compare';

export type ExecutionPlanTabContent = {
  /** Single vs compare view (defaults to 'single') */
  view?: ExecutionPlanWorkspaceView;
  /** Primary plan for single-view */
  plan?: ExecutionPlanArtifact;
  /** Optional source file path (used for session restore) */
  filePath?: string;
  /** Baseline plan for compare-view */
  baselinePlan?: ExecutionPlanArtifact;
  baselineFilePath?: string;
  /** Compare plan for compare-view */
  comparePlan?: ExecutionPlanArtifact;
  compareFilePath?: string;
};

/**
 * Content for table editor tabs (Phase 9)
 */
export type TableEditorTabContent = {
  /** Table name being edited */
  tableName: string;
  /** Schema name */
  schema: string;
  /** Active filters */
  filters: FilterDefinition[];
  /** Pending changes not yet saved */
  pendingChanges: CellChange[];
  /** Loaded data rows */
  rows: Record<string, unknown>[];
  /** Column definitions */
  columns: TableColumn[];
};

/**
 * Filter definition for table editor
 */
export type FilterDefinition = {
  column: string;
  operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'in' | 'null' | 'notNull';
  value?: string | number | null;
};

/**
 * A pending cell change in table editor
 */
export type CellChange = {
  rowIndex: number;
  columnName: string;
  originalValue: unknown;
  newValue: unknown;
};

/**
 * Column definition for table editor
 */
export type TableColumn = {
  name: string;
  dataType: string;
  nullable: boolean;
  isPrimaryKey: boolean;
};

// ============================================================================
// Table Explorer Types (Phase 10)
// ============================================================================

/**
 * Column definition for table explorer
 */
export type TableExplorerColumn = {
  name: string;
  dataType: string;
  nullable: boolean;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
  /** True when isForeignKey came from a user-declared loose relationship, not a real FK constraint. */
  isLoose?: boolean;
  /** Whether this column is an identity/auto-increment column */
  isIdentity?: boolean;
  /**
   * True when the engine type is a native enum (Postgres `pg_type.typtype='e'`).
   * Rescues PG enum columns whose `dataType` is the enum's own type name (via
   * `format_type`) and so can't be detected from the string alone.
   */
  isNativeEnumType?: boolean;
  /** Referenced schema for FK columns */
  referencedSchema?: string;
  /** Referenced table for FK columns */
  referencedTable?: string;
  /** Referenced column for FK columns */
  referencedColumn?: string;
  /**
   * JSON column classification (engine-native / CHECK ISJSON / MetaInfo-declared).
   * Populated by `useTableMetadata` via `classifyJsonColumn`. Drives Slice E3
   * JSON-aware filter operator dropdown in `FilterChip` /  `DraftFilterChip`.
   */
  jsonClassification?: JsonClassification;
};

/**
 * Sort definition for table explorer
 */
export type SortDefinition = {
  field: string;
  direction: 'asc' | 'desc';
};

// ============================================================================
// Table Explorer Edit Mode Types
// ============================================================================

/**
 * A pending change in table explorer edit mode.
 * Tracks INSERT, UPDATE, and DELETE operations.
 */
export type PendingChange =
  | PendingInsert
  | PendingUpdate
  | PendingDelete;

/**
 * A pending INSERT operation (new row)
 */
export type PendingInsert = {
  type: 'insert';
  /** Temporary ID for the new row (client-generated) */
  tempId: string;
  /** Row data to insert */
  row: Record<string, unknown>;
};

/**
 * A pending UPDATE operation (cell edit)
 */
export type PendingUpdate = {
  type: 'update';
  /** Primary key value(s) identifying the row */
  primaryKey: Record<string, unknown>;
  /** Column name being updated */
  field: string;
  /** Original value before edit */
  oldValue: unknown;
  /** New value after edit */
  newValue: unknown;
};

/**
 * A pending DELETE operation
 */
export type PendingDelete = {
  type: 'delete';
  /** Primary key value(s) identifying the row to delete */
  primaryKey: Record<string, unknown>;
  /** Original row data (for display purposes) */
  originalRow: Record<string, unknown>;
};

/**
 * Sub-operator for the `json` filter operator. Selecting `json` reveals a
 * second dropdown with these choices. See `JsonColumnSupport/PRD.md`.
 *
 * - `has_property` / `not_has_property` — does the JSON document have the
 *   given path? Needs `path`, no `value`.
 * - `path_eq` / `path_neq` — value at path equals / doesn't equal `value`.
 * - `path_contains` — value at path is a string containing `value`.
 * - `path_gt` / `path_gte` / `path_lt` / `path_lte` — numeric/date
 *   comparison on the value at path.
 * - `is_empty` — top-level value is `{}` or `[]`. No path, no value.
 * - `is_valid` / `is_not_valid` — JSON validity check on the column. No path,
 *   no value. Most useful for declared-on-text columns.
 * - `property_lookup` / `any_lookup` / `all_lookup` — value at path equals a
 *   scalar supplied by the FK row picker (loose-FK lookup against a related
 *   table). `property_lookup` is the scalar shape (mirrors `path_eq`);
 *   `any_lookup` / `all_lookup` are the wildcard aggregates (mirror
 *   `any_eq` / `all_eq`).
 */
export type JsonSubOperator =
  | 'has_property'
  | 'not_has_property'
  | 'path_eq'
  // Case-insensitive scalar equality at a path (PostgreSQL 19+ only). Emits a
  // `jsonb_path_exists(col, '<path> ? (@.lower() == "<v>")')` predicate — the
  // value is lowercased app-side. Gated to pgsql ≥ 19 by
  // `isJsonpathMethodsSupported`; other engines never surface it and the SQL
  // emit throws defensively if reached.
  | 'path_ieq'
  | 'path_neq'
  | 'path_is_null'
  | 'path_is_not_null'
  | 'path_contains'
  | 'path_gt'
  | 'path_gte'
  | 'path_lt'
  | 'path_lte'
  | 'is_empty'
  | 'is_not_empty'
  | 'is_valid'
  | 'is_not_valid'
  | 'any_eq'
  // Case-insensitive equality on any wildcard element (PostgreSQL 19+ only).
  // The `[*]` wildcard stays inside the jsonpath:
  // `jsonb_path_exists(col, '$.arr[*].sku ? (@.lower() == "<v>")')`. Same
  // pgsql ≥ 19 gate as `path_ieq`.
  | 'any_ieq'
  | 'any_contains'
  | 'any_gt'
  | 'any_gte'
  | 'any_lt'
  | 'any_lte'
  | 'all_eq'
  | 'all_gt'
  | 'all_gte'
  | 'all_lt'
  | 'all_lte'
  | 'property_lookup'
  | 'any_lookup'
  | 'all_lookup'
  | 'property_enum'
  | 'any_enum'
  | 'all_enum';

/**
 * Spatial filter sub-operator (only used when `operator === 'spatial'`).
 *
 * - `intersects_bbox` — the geometry intersects a map-viewport bounding box
 *   (`bbox`). Emitted per engine by `buildSpatialConditionSql` (PostGIS
 *   `ST_Intersects` + `&&`, MySQL `MBRIntersects`, Oracle `SDO_GEOM.RELATE`,
 *   MSSQL `STIntersects`, SpatiaLite `MbrIntersects`).
 * - `within_distance` — the geometry lies within `distanceMeters` of `point`
 *   (`ST_DWithin`::geography, `ST_Distance_Sphere`, `SDO_GEOM.SDO_DISTANCE`,
 *   `STDistance`). Not offered on engines/CRS the verified forms can't express
 *   safely (e.g. a non-geographic PostGIS geometry column).
 * - `intersects_polygon` — the geometry intersects a user-DRAWN polygon
 *   (`polygon`, a canonical CCW-open lng/lat ring). Emitted by
 *   `buildPolygonSql` (PostGIS `ST_Intersects` + `ST_GeomFromText`/
 *   `ST_GeogFromText`, MySQL `ST_Intersects`, Oracle `SDO_GEOM.RELATE`, MSSQL
 *   `STIntersects`). Not offered for SQLite / Kusto / declared pairs, nor for
 *   engines that require SRID 4326 on a non-4326 column.
 */
export type SpatialSubOperator = 'intersects_bbox' | 'within_distance' | 'intersects_polygon';

/**
 * Emit-dispatch + capability context for a spatial filter chip, stamped when
 * the chip is created from the map pane (T14 viewport / map-click). `srid` is
 * the loaded window's SRID; when the window carries more than one distinct
 * SRID it is stamped `null` (never a guessed dominant SRID) and the emitter
 * falls back to the engine's verified default or hides the option.
 */
export type SpatialFilterContext = {
  kind: 'geometry' | 'geography' | 'declared-pair';
  srid: number | null;
  /** `declared-pair` only — the sibling longitude column (T19 quotes/emits it). */
  lngColumn?: string;
};

/**
 * Filter condition for table explorer
 *
 * `lookup` is FK-aware sugar for `=`: it stores the same scalar value but the
 * UI renders an FK row picker instead of a free-text input. SQL generation
 * treats `lookup` identically to `=`.
 *
 * Array operators (Postgres `int[]`, `uuid[]`, `text[]`, …):
 * - `array_contains` — `<value> = ANY(<field>)`. Doubles as the FK-picker
 *   surface on array FK columns and the loose-FK declaration entry point on
 *   non-FK array columns. `value` is a scalar element.
 * - `array_not_contains` — `<value> <> ALL(<field>)`. `value` is a scalar element.
 * - `array_is_empty` — `cardinality(<field>) = 0`. No value.
 * - `array_is_not_empty` — `cardinality(<field>) > 0`. No value.
 * - `array_length_eq | _neq | _gt | _lt | _gte | _lte` — `cardinality(<field>) <op> <n>`.
 *   `value` is a non-negative integer.
 *
 * Non-Postgres engines have no native array columns, so these operators never
 * surface in the UI for them. SQL emit still produces a legal-ish fallback.
 *
 * JSON operator (`json`): coexists with the others in the dropdown for
 * native-JSON or declared-JSON columns. When `operator === 'json'`, the
 * `jsonSubOperator` discriminates the predicate, `path` carries the JSONPath
 * (`$.address.city` or `address.city` — normalized at SQL-emit time), and
 * `value` carries the comparand for sub-ops that need one. `jsonDeclaredOnText`
 * marks the column as declared-on-text so SQL emit wraps the predicate with
 * the engine's JSON-validity guard (MSSQL `ISJSON`, Oracle `IS JSON`,
 * SQLite `json_valid`).
 *
 * Enum operator (`enum`): a dropdown-backed equality filter for columns that
 * hold a finite value set (native enum, declared enum, or a sampled-eligible
 * column). SQL-emits identical to `=` (`<field> = <value>`); the difference
 * is purely the value-input UX — `EnumValuePicker` instead of free text.
 * `=`/`!=` deliberately stay free-text so users can still type ad-hoc values.
 *
 * List operators (`IN` / `NOT IN`, labelled "in list" / "not in list"): the
 * value is a `(string | number)[]` the user typed or pasted — `<field> IN (…)`
 * / `<field> NOT IN (…)`, each value with the same literal policies as `=`.
 * An empty list emits no predicate. `NOT IN` keeps SQL's semantics: a NULL
 * cell never matches it (like `!=`). Oracle's 1,000-value list limit is met by
 * the emitters (`inListFilterSql.ts`), not by the model.
 */
export type FilterCondition = {
  operator:
    | '='
    | '<'
    | '<='
    | '>'
    | '>='
    | 'LIKE'
    | 'CONTAINS'
    | '!='
    | 'BETWEEN'
    | 'IN'
    | 'NOT IN'
    | 'IS NULL'
    | 'IS NOT NULL'
    | 'lookup'
    | 'array_contains'
    | 'array_not_contains'
    | 'array_is_empty'
    | 'array_is_not_empty'
    | 'array_length_eq'
    | 'array_length_neq'
    | 'array_length_gt'
    | 'array_length_lt'
    | 'array_length_gte'
    | 'array_length_lte'
    | 'json'
    | 'enum'
    | 'spatial';
  /**
   * The array form widened to `(string | number)[]` (decision #4, on-demand
   * FK labels design) so a big `enum`/lookup `IN` list can carry numeric FK
   * keys and emit them UNQUOTED (sargable) instead of stringifying every
   * value. Existing pickers may keep emitting `string[]` unchanged — the
   * widening only permits numbers, it doesn't require them.
   */
  value?: string | number | bigint | boolean | Date | null | (string | number | bigint)[];
  value2?: string | number | bigint | Date; // For BETWEEN
  /**
   * The column's category, set ONLY by Data Compare's filter bar
   * (`DataCompareFilterWizard` / `useDataCompareFilterDrafts`) so
   * `dataComparer.ts`'s in-memory `=`/`!=`/`IN`/`NOT IN` can compare a value
   * the way the column's type means it — exact decimal for `number`, same
   * instant for `datetime` — instead of the case-insensitive `String()`
   * equality that silently drops rows for a widget-normalized date or decimal
   * (a picked `2024-03-01 00:00` vs. a row's `2024-03-01T00:00:00.000Z`; a
   * typed `1.00` stored as the number `1` vs. a row's `"1.00"`). No SQL emitter
   * reads this field — every other filter surface (Table Explorer, VQE WHERE,
   * the reopen parser, MCP) infers type from the live schema instead, so a
   * condition with no `valueCategory` (every existing session, every other
   * surface) keeps the prior case-insensitive-text behavior unchanged.
   */
  valueCategory?: DataTypeCategory;
  /** JSON sub-operator — only used when `operator === 'json'`. */
  jsonSubOperator?: JsonSubOperator;
  /** JSONPath for the `json` operator (e.g. `$.address.city`). */
  path?: string;
  /** Marks the column as declared-on-text — adds engine validity guard. */
  jsonDeclaredOnText?: boolean;
  /**
   * Optional JSONPath inside the source column for the `lookup` operator.
   * Set when the source column is JSON and the loose FK declares a path
   * (e.g. `metadata` JSON, `sourcePath = $.customer_id`). The SQL builder
   * extracts the value at this path and compares it to `value` instead of
   * comparing the column itself. No-op for non-`lookup` operators.
   */
  sourcePath?: string;
  /** Spatial sub-operator — only used when `operator === 'spatial'`. */
  spatialSubOperator?: SpatialSubOperator;
  /**
   * Bounding box `[minLng, minLat, maxLng, maxLat]` (WGS84), captured from the
   * map viewport for `intersects_bbox`. `minLng > maxLng` (or out-of-range
   * bounds) means the viewport crosses the antimeridian — the EMIT owns the
   * two-envelope split, never the UI (stored exactly as captured).
   */
  bbox?: [number, number, number, number];
  /** Point `[lng, lat]` (WGS84) for `within_distance`, set from a map click. */
  point?: [number, number];
  /** Radius in metres for `within_distance`. */
  distanceMeters?: number;
  /**
   * Canonical CCW-open lng/lat ring for `intersects_polygon`, captured by drawing on the map. The
   * first vertex is NOT repeated (the EMIT closes it per engine) and self-intersecting / degenerate
   * / antimeridian-crossing / >200-vertex rings are rejected before they reach here
   * (`validatePolygonRing`). The session coercer re-validates on load.
   */
  polygon?: [number, number][];
  /** Emit-dispatch + capability context for the `spatial` operator. */
  spatialContext?: SpatialFilterContext;
};

/**
 * Filter group for table explorer (conditions within a group are OR-ed)
 */
export type FilterGroup = {
  field: string;
  conditions: FilterCondition[];
};

// ============================================================================
// FK Breadcrumb — Navigation Tree (Slice 3)
// ============================================================================

/**
 * Stable id for a navigation node. Currently UUID-shaped strings.
 */
export type NavigationNodeId = string;

/**
 * A table-node in the navigation tree. Represents a Table Explorer view —
 * either the root of the tree (unfiltered table) or a filtered view (reached
 * via a reverse-FK hop from a parent row).
 */
export type TableNode = {
  readonly kind: 'table';
  readonly id: NavigationNodeId;
  readonly parentId: NavigationNodeId | null;
  readonly childIds: ReadonlyArray<NavigationNodeId>;

  readonly schema: string;
  readonly table: string;
  /**
   * Locked filters applied to this view — e.g. the inbound-FK constraint
   * inherited from the parent row when the user drilled in. Rendered as
   * read-only "locked" chips in the FilterWizard; users cannot edit or
   * remove these. (Field name is `filters` rather than `lockedFilters` for
   * backwards compatibility with persisted sessions.)
   */
  readonly filters: ReadonlyArray<FilterGroup>;
  /**
   * User-added filters that live on this node. Persisted so the user's
   * additions survive deactivation/reactivation of the node.
   */
  readonly userFilters?: ReadonlyArray<FilterGroup>;
  /**
   * User-added sorting that lives on this node. Persisted alongside
   * `userFilters` for the same reason.
   */
  readonly userSorting?: ReadonlyArray<SortDefinition>;
  /** If reached via reverse-FK, the FK column on this table. */
  readonly reverseFkColumn?: string;
  /** Marks pure-m2m join tables; suppressed from chip rendering by default. */
  readonly isJoinCollapsed: boolean;
  /** Optional human label for the chip (table title). */
  readonly displayLabel?: string;
  /**
   * Per-node edit-mode state. Each TableNode (root or drilled) owns its own
   * edit mode and pending changes — saving targets THIS node's table, not the
   * tab's original root table. Activating a different chip preserves both
   * sides' edit state so the user can switch contexts without losing work.
   * Row-nodes are never editable; only TableNodes carry this state.
   */
  readonly isEditMode?: boolean;
  readonly pendingChanges?: ReadonlyArray<PendingChange>;
};

/**
 * A row-node in the navigation tree. Represents the Single-Row Details View
 * for a specific row, identified by its primary key.
 */
export type RowNode = {
  readonly kind: 'row';
  readonly id: NavigationNodeId;
  readonly parentId: NavigationNodeId | null;
  readonly childIds: ReadonlyArray<NavigationNodeId>;

  readonly schema: string;
  readonly table: string;
  /** Composite primary key as ordered (column, value) pairs. */
  readonly pk: ReadonlyArray<{ readonly column: string; readonly value: unknown }>;
  /** Optional human label for the chip (e.g. customer.full_name); cached on first load. */
  readonly displayLabel?: string;
};

export type NavigationNode = TableNode | RowNode;

/**
 * A breadcrumb navigation tree for a single Table Explorer tab.
 *
 * Slice 3 only uses stack-shaped trees (each node has 0–1 children). Slice 4
 * adds branching (multiple `childIds`). Siblings live in their parent's
 * `childIds` array.
 */
export type NavigationTree = {
  readonly rootId: NavigationNodeId;
  readonly activeNodeId: NavigationNodeId;
  /** Nodes keyed by id. */
  readonly nodes: Readonly<Record<NavigationNodeId, NavigationNode>>;
};

/**
 * Content for table explorer tabs
 */
export type TableExplorerTabContent = {
  /** Table name being explored */
  tableName: string;
  /** Schema name */
  schema: string;
  /** Database name */
  database: string;
  /** Loaded data rows */
  rows: Record<string, unknown>[];
  /** Column definitions */
  columns: TableExplorerColumn[];
  /** Total row count in the table (if known) */
  totalRowCount?: number;
  /**
   * APPROXIMATE whole-table row count from the engine's catalog statistics,
   * supplied only when the exact `COUNT(*)` missed its budget on an unfiltered
   * fetch (see `TableExplorerFetchRowsResult.estimatedRowCount`). Drives page
   * math and the "≈" row-range/status display; never used when
   * `totalRowCount` is set.
   */
  estimatedRowCount?: number;
  /** Number of rows to fetch (page size) */
  limit: number;
  /** Current page number (1-indexed) for pagination */
  currentPage?: number;
  /** Selected columns to display ('*' for all) */
  selectedColumns: string[] | '*';
  /** Active filters (groups are AND-ed together) */
  filters: FilterGroup[];
  /** Sorting configuration */
  sorting: SortDefinition[];
  /** Loading state */
  isLoading?: boolean;
  /** Error message if loading failed */
  error?: string;
  // ---- Streaming / Cache State (runtime-only, not persisted) ----
  /** Cache URI when the current page spilled to a windowed source. */
  cacheUri?: string;
  /** Cache mode for the current page: 'in-memory' (rows inlined) or 'windowed' (rows on demand). */
  populationMode?: 'in-memory' | 'windowed';
  /** Bumped on each fetch so usePopulationProgress / useWindowedData re-subscribe cleanly. */
  cacheGeneration?: number;
  // ---- Edit Mode State ----
  /** Whether edit mode is enabled */
  isEditMode?: boolean;
  /** Pending changes (insert/update/delete) not yet saved */
  pendingChanges?: PendingChange[];
  /** Primary key columns for the table (needed for edit operations) */
  primaryKeyColumns?: string[];
  /** Set when tab is restored from session - suppresses analytics tracking */
  restoredFromSession?: boolean;
  /**
   * Runtime-only flag set when a Table Explorer tab is opened from a pinned
   * workspace snapshot (open-from-pin). Signals that the tab's persisted
   * `selectedColumns` / `filters` were captured against an earlier schema and
   * still need reconciling against the live table's columns. NOT serialized —
   * `serializeTab` in `sessionStore.ts` picks fields explicitly, so this never
   * round-trips through a session. Consumed by the reconcile pass (Task 14).
   */
  pendingColumnReconcile?: boolean;
  /**
   * Blueprint-edit mode descriptor. When set, this Table Explorer tab renders
   * the rows captured in a Database Blueprint's `Data/<schema>.<table>.data.json`
   * sidecar instead of querying a live database, and Save writes back to the
   * sidecar + regenerates the `.data.sql` upsert script. The normal
   * `connectionId`/`database`/`schema`/`tableName` fields still carry the
   * linked DB's identity so MetaInfo (JSON/enum/loose-FK overlays) keeps
   * working; they are NOT used to query rows in this mode.
   *
   * Additive + optional: absent on every normal (live-DB) Table Explorer tab.
   */
  blueprintEdit?: {
    /** Absolute path of the blueprint folder root. */
    folderPath: string;
    /** Engine whose upsert dialect the regenerated `.data.sql` uses. */
    engine: UserFacingEngineId;
  };
  /** Slice 6 — surfaces a dismissable banner when a row fetch fails against the tab's scope DB. */
  connectionFailure?: ConnectionFailureBannerState;
  // ---- FK Breadcrumb (Slice 3) ----
  /**
   * The breadcrumb navigation tree for this tab. `null` when the tab was
   * opened directly (no nav history); the first FK hop initializes the tree
   * (root + child) and the chip-row appears.
   *
   * The active view mode is derived from the active node:
   *   - `kind: 'table'` → Grid View
   *   - `kind: 'row'`   → Single-Row Details View
   */
  navigationTree?: NavigationTree | null;
  /**
   * @deprecated Slice 1 shape, kept as read-only fallback for one release.
   * On load, if `viewMode === 'single-row'` and `singleRowRef` is set, a
   * 2-node tree is built and stored in `navigationTree`. Not written to
   * anywhere new — see `migrateLegacyViewMode` in `breadcrumbTree.ts`.
   */
  viewMode?: 'grid' | 'single-row';
  /**
   * @deprecated Slice 1 shape — see `viewMode` above.
   */
  singleRowRef?: {
    readonly pk: ReadonlyArray<{ readonly column: string; readonly value: unknown }>;
  } | null;
};

// ============================================================================
// Table Designer Types (Slice 6)
// ============================================================================

/**
 * Designer mode: 'create' for new tables, 'alter' for existing tables
 */
export type TableDesignerMode = 'create' | 'alter';

/**
 * Column definition in the table designer
 */
export type TableDesignerColumn = {
  /** Unique ID for the column (client-generated) */
  id: string;
  /** Column name */
  name: string;
  /** Data type (e.g., INT, VARCHAR(255), NVARCHAR(MAX)) */
  dataType: string;
  /** Whether the column allows NULL values */
  nullable: boolean;
  /** Whether this column is part of the primary key */
  isPrimaryKey: boolean;
  /** Whether this is an identity/auto-increment column */
  isIdentity: boolean;
  /** Identity seed (starting value) */
  identitySeed?: number;
  /** Identity increment */
  identityIncrement?: number;
  /** Default value expression */
  defaultValue?: string;
  /** Max length for string types (null for MAX) */
  maxLength?: number | null;
  /** Precision for numeric types */
  precision?: number | null;
  /** Scale for numeric types */
  scale?: number | null;
};

/**
 * Original table state for diffing (alter mode only)
 */
export type TableDesignerOriginalState = {
  tableName: string;
  schema: string;
  columns: TableDesignerColumn[];
};

/**
 * Content for table designer tabs.
 *
 * Buffer-backed model (VqeDdlEdit Slice 0.5): the Designer hosts a hidden
 * Monaco-like buffer (`currentSql`) synthesized from `initialSql` for alter
 * mode, or pre-filled with `CREATE TABLE [name] ();` for create mode. Save
 * executes the diff between `initialSql` and `currentSql` against the
 * connection.
 *
 * `columns` / `originalState` remain on the type for session-restore
 * compatibility with persisted designer tabs predating the migration; the
 * runtime no longer drives emit from them.
 */
export type TableDesignerTabContent = {
  /** Designer mode: creating new table or altering existing */
  mode: TableDesignerMode;
  /** Table name */
  tableName: string;
  /** Schema name */
  schema: string;
  /** Database name */
  database: string;
  /**
   * Initial CREATE TABLE script — for alter mode, synthesized from the
   * existing object's metadata via the engine's script provider; for create
   * mode, the blank `CREATE TABLE [name] ();` template. Acts as the diff
   * baseline for Save.
   */
  initialSql?: string;
  /**
   * Current buffer text — the user's working CREATE TABLE script. Driven by
   * the hidden Monaco buffer; persisted across rerenders via the workspace
   * store. Save executes the diff against `initialSql`.
   */
  currentSql?: string;
  /**
   * Column renames the visual editor applied to the draft since the last
   * successful save, oldest first. Save hands them to the planner so a rename
   * stays a RENAME COLUMN statement instead of being inferred — or turned into
   * a DROP + ADD — from the edited definition. Cleared when the baseline
   * advances.
   */
  columnRenames?: DesignerColumnRename[];
  /**
   * Whether the Designer is showing the hidden buffer in a Monaco view
   * ("View SQL" toggle). Defaults to false (visual UI shown).
   */
  viewSqlMode?: boolean;
  /**
   * Legacy column definitions — retained for session-restore back-compat;
   * runtime does not use this for emit (buffer-backed model owns that).
   */
  columns?: TableDesignerColumn[];
  /** Legacy original state — retained for session-restore back-compat. */
  originalState?: TableDesignerOriginalState;
  /** Whether there are unsaved changes */
  isDirty?: boolean;
  /**
   * True while this tab is still in the state the persist cap left it: its
   * draft exceeded `PERSISTED_SQL_LIMIT_CHARS` and was dropped from the
   * snapshot, and the user has not typed their own definition since. It drives
   * the explanatory banner, the Save-disabled reason and whether the next
   * snapshot re-emits the omission — `onBufferChange` clears it on the first
   * edit, so the notice never becomes permanent noise.
   */
  sqlOmitted?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Error message if loading failed */
  error?: string;
  /** Whether we're currently applying changes */
  isApplying?: boolean;
  /**
   * When set, the columns grid scrolls + briefly highlights the matching
   * column row on mount. Cleared by the grid after the focus effect runs.
   * Used by Go-to-def column navigation (navigationalFeatures Slice 6).
   */
  focusColumnName?: string;
};

// ============================================================================
// Security Manager Types
// ============================================================================

/**
 * Security manager view mode
 */
export type SecurityManagerView = 'server' | 'database';

/**
 * Security object types that can be managed
 */
export type SecurityObjectType =
  // MSSQL server-level
  | 'login'
  | 'server-role'
  // MSSQL database-level
  | 'user'
  | 'database-role'
  // Shared
  | 'schema'
  // PostgreSQL
  | 'login-role'
  | 'group-role'
  // MySQL
  | 'mysql-user'
  // Oracle
  | 'oracle-user'
  | 'oracle-role';

/**
 * Content for security manager tabs
 */
export type SecurityManagerTabContent = {
  /** Current view: server-level or database-level objects */
  view: SecurityManagerView;
  /** Current security object type being managed */
  objectType: SecurityObjectType;
  /** Selected database for database-level objects */
  selectedDatabase?: string;
  /** Currently selected object ID for editing (null = list view) */
  selectedObjectId?: string;
  /** Whether we're in create mode (vs edit mode) */
  isCreateMode?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Error message if loading failed */
  error?: string;
  /** Whether there are unsaved changes */
  isDirty?: boolean;
  /** Whether we're currently applying changes */
  isApplying?: boolean;
};

// ============================================================================
// Schema Compare Types
// ============================================================================

/**
 * Object type for single-object schema comparison
 */
export type SchemaCompareObjectType =
  | 'table'
  | 'view'
  | 'procedure'
  | 'function'
  | 'sequence'
  | 'userType';

/**
 * Per-endpoint schema include/exclude filter.
 * Mirror of SchemaFilter in src/features/workspace/schema-compare/types/schemaCompare.ts
 * — kept inline here to avoid cross-feature import for a 3-line type.
 */
export type SchemaFilter = {
  mode: 'include' | 'exclude';
  schemas: string[];
};

/**
 * Source/target selection for schema comparison.
 *
 * The endpoint can resolve to one of:
 *   - a database (the historical default — `connectionId` + `database`, no folderPath),
 *   - a Database Blueprint folder (`folderPath` set; `connectionId`/`database` empty).
 *
 * `sourceKind` is the optional explicit discriminator. When omitted, the
 * presence of `folderPath` means "folder", otherwise "database". This keeps
 * every existing call-site working without a forced migration.
 */
export type SchemaCompareEndpoint = {
  /** Explicit discriminator. Defaults to inference from folderPath when omitted. */
  sourceKind?: 'database' | 'folder';
  /** Connection ID for this endpoint (database mode). */
  connectionId?: string;
  /** Database name to compare (database mode). */
  database?: string;
  /** Absolute path to a Database Blueprint folder (folder mode). */
  folderPath?: string;
  /** Linked-DB label inside the blueprint — defines the dialect for the folder snapshot. */
  blueprintLinkLabel?: string;
  // Single-object selection (optional)
  /** Object type for single-object comparison */
  objectType?: SchemaCompareObjectType;
  /** Schema of the object */
  objectSchema?: string;
  /** Name of the object */
  objectName?: string;
  /** Per-endpoint schema include/exclude filter. */
  schemaFilter?: SchemaFilter;
};

/**
 * True if the endpoint resolves to a Database Blueprint folder snapshot rather
 * than a live database.
 */
export function isFolderEndpoint(
  endpoint: SchemaCompareEndpoint | undefined,
): boolean {
  if (!endpoint) return false;
  if (endpoint.sourceKind === 'folder') return true;
  if (endpoint.sourceKind === 'database') return false;
  return !!endpoint.folderPath;
}

/**
 * Schema compare options (persisted per tab)
 */
export type SchemaCompareOptions = {
  /** Ignore case differences in identifiers */
  ignoreCase?: boolean;
  /** Ignore whitespace in view/routine definitions */
  ignoreWhitespace?: boolean;
  /**
   * When true, run view/routine/trigger DDL through the engine-aware SQL
   * formatter before comparing — so two procs that differ only in indentation
   * or keyword case collapse to "identical". Default true. When false the
   * comparator falls back to char-by-char on the raw DDL (subject to
   * `ignoreWhitespace`).
   */
  formatSqlForComparison?: boolean;
  /** Include tables in comparison (default: true) */
  includeTables?: boolean;
  /** Include views in comparison (default: true) */
  includeViews?: boolean;
  /** Include stored procedures in comparison (default: true) */
  includeProcedures?: boolean;
  /** Include functions in comparison (default: true) */
  includeFunctions?: boolean;
  /**
   * Include sequences in comparison (default: true). Present on the IPC DTO
   * (`SchemaCompareOptionsDto`) since the fetcher gates on it, but missing
   * from this tab-level type until F07 — which meant no renderer surface could
   * ever switch sequences off. The folder→DB route needs it: a blueprint that
   * does not model sequences must not have the target's sequences fetched,
   * or they come back target-only and pollute the verdict.
   *
   * Offered in the UI only for engines whose fetcher actually enumerates
   * sequences — see `ENGINE_OBJECT_TYPES` in
   * `schema-compare/utils/compareDepth.ts`.
   */
  includeSequences?: boolean;
  /**
   * Include SQL Server user-defined types (table / alias / CLR types) in the
   * comparison (default: true). Offered in the UI for SQL Server only — see
   * `ENGINE_OBJECT_TYPES` in `schema-compare/utils/compareDepth.ts` (S7).
   */
  includeUserTypes?: boolean;
  /** When true, do not auto-exclude system schemas (sys, INFORMATION_SCHEMA, …). Default false. */
  includeSystemSchemas?: boolean;
  /** Property keys to suppress when comparing (e.g. fillFactor, autoIncrement). */
  ignoredProperties?: string[];
  /**
   * "Ignore object owner" — the schema qualifier in view/procedure/function/
   * trigger definition headers carries no verdict weight (Soporte 9 F1).
   * Compare-relevant, so it lives on `SchemaCompareOptionsDto` too — see that
   * field's JSDoc for the semantics. Default false.
   */
  ignoreOwner?: boolean;
  /**
   * When true, fetch full per-table metadata: indexes, foreign keys, triggers,
   * engine-specific table options, partition definition, column extras
   * (computed/identity/collation/comment). When false, only column-level diffs
   * are produced — much faster on large schemas. Default true.
   */
  detailed?: boolean;
  /**
   * "Wrap in a single transaction" — whether Generate Script emits an
   * all-or-nothing sync script (R3-2, showcase review round 3).
   *
   * ## The one field here that is NOT a compare input
   *
   * Every other field on this type changes what gets fetched or what counts as
   * a difference. This one changes nothing about the comparison: it is read
   * only at script-generation time, by `handleGenerateScript` →
   * `useScriptGeneration` → the generate-script IPC's own `wrapInTransaction`.
   * It lives here anyway, and deliberately, because "the compare options" is
   * the bag the user can EXPORT (`compareOptionsFile.ts`), LOAD, and SAVE AS
   * DEFAULTS per engine (`schemaCompareOptionDefaultsStore.ts`) — and a team
   * that shares a comparison profile wants "and the script is atomic" to travel
   * with it. It used to be a bare checkbox on the results toolbar, where it
   * could be neither shared nor defaulted.
   *
   * That exception is load-bearing and has TWO enforced consequences:
   *
   *  1. It is absent from `SchemaCompareOptionsDto` (the compare IPC type), so
   *     `schemaCompareOptionsEqual` cannot see it and a wrap toggle can never
   *     be mistaken for an options change that invalidates the last scan. See
   *     that file's compile-time classification gate — a NEW field here must be
   *     declared either compare-relevant (put it on the DTO) or script-only
   *     (list it there), or the build fails.
   *  2. `canRediffFromCache.deriveFetchShape` never reads it either, for the
   *     same reason: no value of this flag can make a cached snapshot unusable.
   *
   * **Absent means ON for the wrap-capable engines** (`WRAP_CAPABLE_ENGINES` —
   * SQL Server and PostgreSQL) and OFF everywhere else, where the control is
   * not rendered at all. The default is resolved at read time rather than
   * materialized into this object, because the engine a comparison targets is
   * not always known when its options are created — and because an options
   * FILE or a saved default written on SQL Server must not silently impose
   * `true` when it is later loaded against MySQL. An explicit `false` is a user
   * who unticked the box and always wins.
   */
  wrapScriptInTransaction?: boolean;
};

/**
 * Content for schema compare tabs
 */
export type SchemaCompareTabContent = {
  /** Source endpoint (where changes come from) */
  source?: SchemaCompareEndpoint;
  /** Target endpoint (where changes will be applied) */
  target?: SchemaCompareEndpoint;
  /** Comparison options */
  options?: SchemaCompareOptions;
  /** When true, compare entire databases; when false, compare single object (default: true) */
  compareWholeDatabase?: boolean;
  /** Whether a comparison has been run */
  hasCompared?: boolean;
  /** Whether a comparison is currently running */
  isComparing?: boolean;
  /** Error message if comparison failed */
  error?: string;
  /**
   * Placeholder for diff results - full diff payloads are NOT persisted.
   * After session restore, show "Run Compare to refresh" banner.
   */
  needsRefresh?: boolean;
  /**
   * Direction the generated sync script flows. `'source-to-target'` (default)
   * mutates the target to match the source — the historical behaviour. The
   * toggle lets the user reverse the script's destination without re-running
   * the compare. The visual diff stays Source-on-the-left regardless — only
   * the generated script changes.
   */
  scriptDirection?: 'source-to-target' | 'target-to-source';
  /**
   * **LEGACY — read on restore, never written any more.** R3-2 moved "Wrap in a
   * single transaction" into the compare options, where it can be exported,
   * loaded and saved as a per-engine default:
   * `SchemaCompareOptions.wrapScriptInTransaction` is the live home.
   *
   * Kept because it is what a session written by 1.4.17/1.4.18-era builds has
   * on disk, and `sessionStore.serializeTab` + `tabDeserializer` still
   * round-trip it. A user who unticked the box before the move must keep
   * getting an unwrapped script after it — silently re-imposing the default
   * over a stored preference is a worse bug than the layout this move fixed.
   *
   * Read ONLY through `schemaCompareOptionsWithWrap`
   * (`schema-compare/utils/wrapInTransactionOption.ts`), which folds it into
   * `options` for callers, with `options` winning. The first confirm through
   * the Setup dialog writes the value into `options` and this field is shadowed
   * from then on. Nothing may write it.
   */
  wrapScriptInTransaction?: boolean;
  /**
   * When true, SchemaCompareTab auto-runs the comparison once on open (as soon
   * as both endpoints are ready), instead of waiting for the user to click
   * "Compare". Set by the Database Blueprint "Apply schema to DB" flow, which
   * opens the tab with a folder source + DB target already known. Fires at most
   * once; the normal manual flow leaves this undefined.
   */
  autoCompare?: boolean;
  /**
   * Object-type flags in `options` that a Database Blueprint manifest imposed
   * as a SCOPE CAP, rather than the user choosing them (W3.4/F07).
   *
   * Recorded rather than re-derived. The previous version inferred "these are
   * caps" from the endpoint kinds at the moment a dialog was confirmed, which
   * stops being true the instant the user re-points a folder tab at a live
   * database: the tab then holds DB-shaped endpoints with the manifest's caps
   * still sitting in `options`, and the NEXT confirm would happily persist them
   * as that engine's default. Intent has to be carried, not reconstructed from
   * shape one session later.
   *
   * `SchemaCompareTab` narrows the list on every confirm — a flag the user
   * changed is theirs from then on and drops out of here.
   */
  scopeCapKeys?: SchemaCompareScopeCapKey[];
};

/**
 * The object-type options a blueprint manifest can cap. Same five the emitter
 * decides file-by-file (`blueprintEmitsFiles`), expressed as option keys.
 */
export const SCHEMA_COMPARE_SCOPE_CAP_KEYS = [
  'includeTables',
  'includeViews',
  'includeProcedures',
  'includeFunctions',
  'includeSequences',
  'includeUserTypes',
] as const;

export type SchemaCompareScopeCapKey = (typeof SCHEMA_COMPARE_SCOPE_CAP_KEYS)[number];

// ============================================================================
// Data Compare Types
// ============================================================================

/**
 * Source or target endpoint for data comparison — discriminated union.
 *
 * - `'connection'`: live database table reached through a saved connection.
 * - `'blueprint-folder'`: `.data.sql` sidecar inside a Database Blueprint folder
 *    (Phase 4 wires this into the data-compare engine; Phase 3 only lands
 *    the type + a standalone fetcher).
 */
export type DataCompareConnectionEndpoint = {
  kind: 'connection';
  /** Connection ID (from connectionStore) — empty string means "unconfigured". */
  connectionId: string;
  /** Database name — empty string means "unconfigured". */
  database: string;
  /** Schema name (empty string for SQLite or "unconfigured"). */
  schema: string;
  /** Table name — empty string means "unconfigured". */
  table: string;
};

export type DataCompareBlueprintFolderEndpoint = {
  kind: 'blueprint-folder';
  /** Absolute path to the blueprint folder root. */
  folderPath: string;
  /** Schema name (empty string for engines without schemas). */
  schema: string;
  /** Table name. */
  table: string;
};

export type DataCompareEndpoint =
  | DataCompareConnectionEndpoint
  | DataCompareBlueprintFolderEndpoint;

export function isConnectionEndpoint(
  e: DataCompareEndpoint | undefined | null,
): e is DataCompareConnectionEndpoint {
  return !!e && e.kind === 'connection';
}

export function isBlueprintFolderEndpoint(
  e: DataCompareEndpoint | undefined | null,
): e is DataCompareBlueprintFolderEndpoint {
  return !!e && e.kind === 'blueprint-folder';
}

/** Default empty endpoint used by setup UI; treated as 'unconfigured'. */
export function emptyConnectionEndpoint(): DataCompareConnectionEndpoint {
  return { kind: 'connection', connectionId: '', database: '', schema: '', table: '' };
}

/**
 * A key column mapping between source and target tables.
 */
export type DataCompareKeyMapping = {
  /** Source column name */
  sourceColumn: string;
  /** Target column name */
  targetColumn: string;
};

/**
 * A value column mapping between source and target tables.
 */
export type DataCompareValueMapping = {
  /** Source column name */
  sourceColumn: string;
  /** Target column name */
  targetColumn: string;
  /** Whether this mapping is included in comparison */
  included: boolean;
};

/**
 * Sample mode for quick comparison preview.
 */
export type DataCompareSampleMode = 'first' | 'random';

/**
 * Options for data comparison behavior.
 */
export type DataCompareOptions = {
  /** Case-sensitive string comparison (default: false) */
  caseSensitive?: boolean;
  /** Trim whitespace from strings before comparison (default: true) */
  trimWhitespace?: boolean;
  /** Treat NULL as equal to empty string for string columns (default: false) */
  nullEqualsEmpty?: boolean;
  /** Number of decimal places for numeric precision comparison (undefined = exact) */
  numericPrecision?: number;
  /** Date/time precision for comparison: 'exact', 'seconds', 'minutes', 'hours', 'days' */
  dateTimePrecision?: 'exact' | 'seconds' | 'minutes' | 'hours' | 'days';
  /** Sample mode: 'first' for first N rows, 'random' for random sample */
  sampleMode?: DataCompareSampleMode;
  /** Sample size (default: 1000) */
  sampleSize?: number;
};

/**
 * Content for data compare tabs
 */
/**
 * Summary of data comparison results (mirrored from electron/data-compare/types.ts)
 */
export type DataCompareSummary = {
  /** Rows only in source (to be INSERTed into target) */
  onlyInSource: number;
  /** Rows only in target (to be DELETEd from target) */
  onlyInTarget: number;
  /** Rows with different values (to be UPDATEd in target) */
  modified: number;
  /** Rows with identical values */
  identical: number;
  /** Total rows processed from source */
  sourceRowCount: number;
  /** Total rows processed from target */
  targetRowCount: number;
  /** Comparison timestamp */
  comparedAt: string;
  /** Whether this is a sample result */
  isSampleResult?: boolean;
  /** Sample size that was used (if sample comparison) */
  sampleSize?: number;
  /** Which sampling strategy produced this result (present when isSampleResult) */
  sampleMode?: DataCompareSampleMode;
  /**
   * Exact `COUNT(*)` of the source table at compare time (sample results only;
   * `null` when the probe failed). Lets the UI say how many rows a sample did
   * NOT check instead of claiming a table-level all-clear (Soporte 8 F2).
   */
  sourceTotalRowCount?: number | null;
  /** Exact `COUNT(*)` of the target table at compare time — see sourceTotalRowCount. */
  targetTotalRowCount?: number | null;
};

/**
 * Sort direction for data compare results
 */
export type DataCompareSortDirection = 'asc' | 'desc';

/**
 * Single column sort specification for data compare results
 */
export type DataCompareSortItem = {
  /** Column ID to sort by (e.g., "key_id", "val_name", or "__status" for diff type) */
  columnId: string;
  /** Sort direction */
  direction: DataCompareSortDirection;
};

export type DataCompareTabContent = {
  /** Source endpoint (where data comes from) */
  source?: DataCompareEndpoint;
  /** Target endpoint (where changes will be applied) */
  target?: DataCompareEndpoint;
  /** Key column mappings for row matching */
  keyMappings?: DataCompareKeyMapping[];
  /** Value column mappings for comparison */
  valueMappings?: DataCompareValueMapping[];
  /** Comparison options */
  options?: DataCompareOptions;
  /** Whether a comparison has been run this session */
  hasCompared?: boolean;
  /** Whether a comparison is currently running */
  isComparing?: boolean;
  /** Error message if comparison failed */
  error?: string;
  /**
   * Flag indicating diff payloads are NOT persisted.
   * After session restore, show "Run Compare to refresh" banner.
   */
  needsRefresh?: boolean;
  /**
   * When true, DataCompareTab auto-runs the comparison once on open (as soon
   * as key mappings are present), then clears the flag. Set by the Database
   * Blueprint "Open in Data Compare" launch. Mirrors SchemaCompareTabContent.autoCompare.
   */
  autoCompare?: boolean;
  /** Operation ID for the current/last comparison (used for paging results) */
  operationId?: string;
  /** Summary of comparison results (counts by diff type) */
  summary?: DataCompareSummary;
  /** Currently selected diff type tab */
  activeDiffTab?: 'all' | 'onlyInSource' | 'onlyInTarget' | 'modified' | 'identical';
  /** Currently selected row key for diff panel (JSON-serialized key values) */
  selectedRowKey?: string;
  /** Selected row keys for script generation (persisted as array for JSON serialization) */
  selectedRowKeys?: string[];
  /**
   * F11 (Soporte round 4b) — `diffKey`s of matched ("Modified") rows the user
   * switched from UPDATE to INSERT via "Apply as INSERT (new row)".
   *
   * Why per-tab and not per-operation: the reporter's case is a repeated
   * test→prod copy, and re-running Compare must not silently discard the
   * decisions he already made. The list therefore SURVIVES a re-diff — but only
   * as a set of keys: the script generator applies an entry only while it still
   * resolves to a `modified` row of the current operation, so a key that stopped
   * matching is inert rather than re-pointed at a different row.
   *
   * It is cleared outright wherever ROW IDENTITY itself changes meaning —
   * re-pointing the tab at a different source/target pair, or changing the key
   * mappings that define what a `diffKey` is.
   */
  insertOverrideKeys?: string[];
  /** Whether the current results are from a sample comparison */
  isSampleResult?: boolean;
  /** Number of rows sampled (if sample comparison) */
  sampledRowCount?: number;
  /** Current sorting state (array for multi-column sort, first item is primary) */
  sorting?: DataCompareSortItem[];
  /** Active filters for results (multiple FilterGroups are AND-ed) */
  filters?: FilterGroup[];
  /**
   * Per-diff-type row count, as last reported by the results grid's page-0
   * fetch for whichever tab is/was active — NOT "the counts once a filter is
   * applied" despite the name. `DataCompareResultsGrid` fires this on every
   * page-0 fetch (`getDiffPage`), filter active or not, for `activeDiffTab`
   * only. A user who never visits e.g. the "Modified" tab leaves `modified`
   * unset here even though real modified rows exist — every field is
   * genuinely optional, not "optional until first render". T13 fix round 2
   * (NF-1): a prior version declared every field required and papered over
   * the mismatch with an `as` cast at the one write site
   * (`DataCompareTab.tsx`'s `handleFilteredCountChange`); that cast is what
   * let a partial object silently pass as a complete one. Do not treat a
   * present-but-partial `filteredCounts` as "the whole diff, filtered" —
   * only fields that are actually set are trustworthy.
   */
  filteredCounts?: {
    all?: number;
    onlyInSource?: number;
    onlyInTarget?: number;
    modified?: number;
    identical?: number;
  };
};

// ============================================================================
// Dependency Viewer Types
// ============================================================================

/**
 * Database object type for dependency tracking
 */
export type DependencyObjectType = 'table' | 'view' | 'procedure' | 'function' | 'trigger' | 'index' | 'constraint' | 'synonym' | 'unknown';

/**
 * A single dependency relationship
 */
export type DependencyItem = {
  /** Object name */
  name: string;
  /** Schema name */
  schema: string;
  /** Type of the object */
  objectType: DependencyObjectType;
  /** Column name if dependency is at column level */
  columnName?: string;
  /** True when this entry came from a user-declared loose relationship in MetaInfo, not a real dependency. */
  viaLooseRelationship?: boolean;
  /** Human-readable label for the loose FK edge (e.g. "field = value" or "schema.table"). */
  looseRelLabel?: string;
  /** True when the loose FK that produced this edge has a sourceFilter or targetFilter. */
  looseRelHasFilter?: boolean;
};

/**
 * Dependencies data structure
 */
export type DependencyData = {
  /** Objects that this object references (uses) */
  uses: DependencyItem[];
  /** Objects that reference this object (used by) */
  usedBy: DependencyItem[];
};

/**
 * Filter state for dependency viewer
 */
export type DependencyFilterState = {
  /** Search text to filter by name */
  searchText: string;
  /** Filter by object type (empty = all types) */
  typeFilter: DependencyObjectType | '';
};

/**
 * Which view the Dependency Viewer tab is showing.
 *
 * `find-usages` (F09 / B7) is a definition-TEXT search rather than a catalog
 * dependency read: it answers "which module bodies mention this text", which
 * the name filter on the other two modes can never answer.
 */
export type DependencyViewMode = 'tree' | 'graph' | 'find-usages';

/**
 * Per-object outcome of a find-usages text scan. Three-valued on purpose — an
 * object whose definition we could not read must not be counted as "no usage".
 * See `findUsagesEngine.ts` for the full rationale.
 */
export type FindUsagesSearchState = 'matched' | 'unreadable' | 'truncated';

/** What a completed find-usages scan produced. */
export type FindUsagesOutcome = {
  /** Objects whose definition text contains the term. */
  items: DependencyItem[];
  /**
   * Objects whose definition could not be read at all (encrypted / no grant).
   *
   * NOTE when `capped` is true this is a floor, not a total: the engine-side cap
   * orders matched rows first, so unreadable rows beyond the cap were never
   * returned to be counted. The UI must say "at least" in that case.
   */
  unreadableCount: number;
  /** Objects searched only up to a prefix (Oracle long views). Same floor caveat as above when capped. */
  truncatedCount: number;
  /** True when the engine-side row cap was reached, so results are partial. */
  capped: boolean;
  /** The cap that was applied, for the honest "first N" wording. */
  limit: number;
  /**
   * Oracle pre-18c only: `ALL_VIEWS.TEXT_VC` does not exist, so the search fell
   * back to routines/packages/triggers and **no view was examined at all**.
   * Silence here would read as "no view uses this column".
   */
  viewsUnsearchable?: boolean;
};

/**
 * What the *database catalog* says references a column (B8 / F09 P3).
 *
 * Deliberately a separate shape from `FindUsagesOutcome`, and rendered as a
 * separate group: a text hit is a lead, a catalog hit is a fact. Merging them —
 * or summing them into one count — would turn a confusing filter into a
 * confidently wrong answer (the F09 probe: 23 text hits, 12 real references).
 */
export type CatalogReferencesOutcome = {
  /** Objects the catalog attributes to this column. */
  columnItems: DependencyItem[];
  /**
   * SQL Server only: objects that reach the column through `SELECT *`. The
   * column is genuinely in scope but is never named, so no text search could
   * find these. PostgreSQL expands `*` at view-creation time and cannot
   * distinguish them, so this is always empty there.
   */
  expandedItems: DependencyItem[];
  /** Objects linked to the table where the catalog cannot attribute a column. */
  wholeObjectItems: DependencyItem[];
  /** Objects whose per-object analysis errored. Unknowns, never zeros. */
  unreadableCount: number;
  /**
   * False when the search term is not a column of the tab's object.
   *
   * Load-bearing: a precise group that renders empty reads as "no references
   * exist". When this is false the UI must say the term is not a column here,
   * not show a confident empty list.
   *
   * Only meaningful when `targetFound` is true — see below.
   */
  columnFound: boolean;
  /**
   * False when the target object itself could not be resolved.
   *
   * Must be checked BEFORE `columnFound`. A blank schema, a dropped or renamed
   * object, or a user without rights to see it all yield `columnFound: false`
   * too — and rendering "that is not a column of dbo.T" on that basis states a
   * fact about the schema that was never established. When this is false the
   * only honest message is "we could not look".
   */
  targetFound: boolean;
  /**
   * True when the answer is partial, for EITHER reason: the result rows hit the
   * cap, or (SQL Server) the candidate-module scan was truncated before every
   * module was analysed. The two need different wording — see `scannedCount`.
   */
  capped: boolean;
  limit: number;
  /** SQL Server: modules actually analysed. */
  scannedCount?: number;
  /** SQL Server: candidate modules the object-level catalog offered. */
  totalCount?: number;
  /** The object the lookup was scoped to, so the UI can name it. */
  targetSchema: string;
  targetObject: string;
};

/** Find-usages state carried by a Dependency Viewer tab. */
export type DependencyFindUsagesState = {
  /** The text being searched for inside SQL definitions. */
  searchTerm: string;
  /** True while a scan is in flight. */
  isSearching?: boolean;
  /** Execution id of the in-flight scan, so it can be cancelled. */
  executionId?: string;
  /** Result of the last completed scan. */
  outcome?: FindUsagesOutcome;
  /** Error from the last scan, if it failed. */
  error?: string;
  /** Result of the last completed catalog lookup (B8). */
  catalog?: CatalogReferencesOutcome;
  /**
   * Error from the catalog lookup. Kept apart from `error` on purpose: the text
   * search failing and the catalog lookup failing are different facts, and one
   * group must still be able to render when the other could not.
   */
  catalogError?: string;
  /** Execution id of the in-flight catalog lookup, so it can be cancelled. */
  catalogExecutionId?: string;
};

/**
 * Navigation entry for dependency viewer back history
 */
export type DependencyViewerNavigationEntry = {
  /** Name of the object being analyzed */
  objectName: string;
  /** Type of the object being analyzed */
  objectType: DependencyObjectType;
  /** Schema of the object */
  schema: string;
  /** Database name */
  database: string;
  /** View mode: tree, graph or find-usages */
  viewMode: DependencyViewMode;
  /** Dependency data once loaded */
  dependencies?: DependencyData;
  /** Error message if loading failed */
  error?: string;
  /** Filter state for search and type filtering */
  filterState?: DependencyFilterState;
  /** Find-usages text-search state, when this entry was in that mode */
  findUsages?: DependencyFindUsagesState;
};

/**
 * Content for dependency viewer tabs
 */
export type DependencyViewerTabContent = {
  /** Name of the object being analyzed */
  objectName: string;
  /** Type of the object being analyzed */
  objectType: DependencyObjectType;
  /** Schema of the object */
  schema: string;
  /** Database name */
  database: string;
  /** View mode: tree, graph or find-usages */
  viewMode: DependencyViewMode;
  /** Dependency data once loaded */
  dependencies?: DependencyData;
  /** Loading state */
  isLoading: boolean;
  /** Error message if loading failed */
  error?: string;
  /** Filter state for search and type filtering */
  filterState?: DependencyFilterState;
  /** Navigation history for back behavior */
  navigationStack?: DependencyViewerNavigationEntry[];
  /**
   * Find-usages text-search state. Present once the user has run (or seeded)
   * a definition-text search on this tab; its presence is also what makes the
   * third segment of the view-mode toggle appear.
   */
  findUsages?: DependencyFindUsagesState;
};

// ============================================================================
// Schema Overview Types
// ============================================================================

/**
 * Filter mode for schema overview.
 * Determines which object types are displayed in the overview.
 */
export type SchemaFilterMode = 'all' | 'tables' | 'views' | 'procedures' | 'functions';

/**
 * Loading step for schema overview progress display.
 */
export type SchemaLoadingStep = 'tables' | 'views' | 'routines' | 'relationships' | 'complete';

/**
 * Schema object type (mirrored from electron/schema-overview/types.ts)
 */
export type SchemaObjectType = 'table' | 'view' | 'procedure' | 'function' | 'trigger';

/**
 * Schema relationship type
 */
export type SchemaRelationshipType =
  | 'foreignKey'
  | 'looseForeignKey'
  | 'viewReference'
  | 'routineReference';

/**
 * A single database object in the schema overview
 */
export type SchemaObject = {
  id: string;
  name: string;
  schema: string;
  type: SchemaObjectType;
  metadata?: {
    rowCount?: number;
    columnCount?: number;
    parameterCount?: number;
  };
};

/**
 * A relationship between two schema objects
 */
export type SchemaRelationship = {
  sourceId: string;
  targetId: string;
  type: SchemaRelationshipType;
  label?: string;
  /** True when the loose FK that produced this edge has a sourceFilter or targetFilter. */
  hasFilter?: boolean;
};

/**
 * Statistics about the schema
 */
export type SchemaStats = {
  tableCount: number;
  viewCount: number;
  procedureCount: number;
  functionCount: number;
  relationshipCount: number;
};

/**
 * Complete schema overview data
 */
export type SchemaOverviewData = {
  objects: SchemaObject[];
  relationships: SchemaRelationship[];
  stats: SchemaStats;
};

/**
 * Object filter for large database loading (type: 'none' | 'all' | string[])
 */
export type SchemaObjectFilter = {
  tables: 'none' | 'all' | string[];
  views: 'none' | 'all' | string[];
  procedures: 'none' | 'all' | string[];
  functions: 'none' | 'all' | string[];
};

/**
 * Content for schema overview tabs
 */
export type SchemaOverviewTabContent = {
  /** Connection ID for the database */
  connectionId: string;
  /** Database name */
  databaseName: string;
  /** Filter mode to limit displayed objects (defaults to 'all') */
  filterMode?: SchemaFilterMode;
  /** Loading state */
  isLoading?: boolean;
  /** Current loading step for progress display */
  loadingStep?: SchemaLoadingStep;
  /** Error message if loading failed */
  error?: string;
  /** Fetched schema data (populated after loading completes) */
  schemaData?: SchemaOverviewData;
  /** Schema counts (for large database filter dialog) */
  schemaCounts?: SchemaStats;
  /** Whether filter dialog should be shown (Pro mode) */
  showFilterDialog?: boolean;
  /** Whether Personal mode limit message should be shown */
  showPersonalModeMessage?: boolean;
  /** User's object filter selection (from filter dialog) */
  objectFilter?: SchemaObjectFilter;
  /** Whether the tab is in dormant state (waiting to be activated on session restore) */
  isDormant?: boolean;
};

// ============================================================================
// Spatial Explorer Types
// ============================================================================

/**
 * Content for a Spatial Explorer tab (T16). One `(connection, database)` per tab; the map view
 * (its layer DEFINITIONS, colours, caps, basemap) lives in the serializable `view`. Feature data is
 * NEVER stored here — layers reload from their table/query source on activate.
 */
export type SpatialExplorerTabContent = {
  /** Connection ID for the database. */
  connectionId: string;
  /** Database name (single database per tab). */
  databaseName: string;
  /** The serializable spatial view — layers + basemap. */
  view: SpatialViewDefinition;
  /** Dormant after session restore — layers load on activate (T17). */
  isDormant?: boolean;
};

// ============================================================================
// Data Profile Types
// ============================================================================

export type DataProfileDataMode = 'server' | 'sample';
export type DataProfileHistogramMode = 'equal-width' | 'quantile';
export type DataProfileHistogramAxis = 'auto' | 'full' | 'trimmed';
export type DataProfileHistogramYScale = 'linear' | 'log';

/**
 * Content for data-profile tabs.
 *
 * Sample rows and aggregation results are NOT persisted on this content (they
 * live in component-local state). On session restore the tab is created with
 * `isDormant: true` and the user must click Reload to refresh the data.
 */
export type DataProfileTabContent = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;

  /** Data source mode (default 'server' — accurate aggregation on the whole table) */
  dataMode: DataProfileDataMode;

  /** Sample size when dataMode === 'sample' */
  sampleSize: number;

  /** Selected column for the right pane (defaults to first column on load) */
  focusedColumn?: string;

  /** Histogram bucket count: 8/16/32, or 0 for "auto" */
  bucketCount: number;
  histogramMode: DataProfileHistogramMode;
  /**
   * Histogram value-axis policy.
   * - 'auto' (default): trim to Tukey fences when extreme skew is detected,
   *   otherwise render the full [min, max] range.
   * - 'full': always render the full [min, max] range, even when skewed.
   * - 'trimmed': always trim to Tukey fences when quartiles are available.
   */
  histogramAxis?: DataProfileHistogramAxis;
  /**
   * Histogram bar-height scale.
   * - 'linear' (default): bar height ∝ count (or density for quantile mode).
   * - 'log': bar height ∝ log10(count + 1) — surfaces tiny outlier buckets
   *   on heavily skewed columns.
   */
  histogramYScale?: DataProfileHistogramYScale;
  /** Top-N entries to show: 5/10/25/50/100 */
  topN: number;

  /** Optional structured filters applied to the table (huge-table escape valve) */
  filters?: FilterGroup[];
  /** Optional explicit row cap; when set, the profile only sees the first N rows */
  rowLimit?: number;

  /** Whether the user has dismissed the large-table hint banner for this tab */
  hideLargeTableHint?: boolean;

  /** Loading + error state (transient; persisted to be honest after restore) */
  isLoading?: boolean;
  loadingStep?: 'fetch-sample' | 'compute-stats';
  error?: string;

  /** Dormant after session restore — user must click Reload to fetch */
  isDormant?: boolean;
};

// ============================================================================
// Start Page Types
// ============================================================================

/**
 * Section visibility state for the start page
 */
export type StartPageExpandedSections = {
  /** Connections section (default: true) */
  connections?: boolean;
  /** Recent tabs section (default: true) */
  recentTabs?: boolean;
  /** Recent scripts section (default: false) */
  recentScripts?: boolean;
  /** Query history section (default: true) */
  queryHistory?: boolean;
};

/**
 * Content for start page tabs
 */
export type StartPageTabContent = {
  /** Section collapse/expand state */
  expandedSections?: StartPageExpandedSections;
  /** Tab search query (persisted for section search) */
  tabSearchQuery?: string;
  /** Selected connection ID for query history filtering */
  selectedConnectionId?: string | null;
};

// ============================================================================
// Package Browser Types (Oracle PL/SQL)
// ============================================================================

export type PackageMemberParam = {
  name: string;
  dataType: string;
  mode: 'IN' | 'OUT' | 'IN/OUT';
  defaultValue?: boolean;
};

/**
 * A procedure or function member within a PL/SQL package
 */
export type PackageMember = {
  name: string;
  type: 'procedure' | 'function';
  overloadIndex?: number;
  params?: PackageMemberParam[];
  returnType?: string;
};

/**
 * A compilation error from ALL_ERRORS
 */
export type PackageCompilationError = {
  line: number;
  position: number;
  text: string;
  attribute: 'ERROR' | 'WARNING';
  messageNumber?: number;
};

// ============================================================================
// PL/SQL Debugger Types (canonical definitions in pl-sql-debugger/types.ts)
// ============================================================================

import type {
  PlSqlDebuggerObjectType as _PlSqlDebuggerObjectType,
  PlSqlDebugState as _PlSqlDebugState,
  ProgramIdentity as _ProgramIdentity,
  PlSqlDebuggerTabContent as _PlSqlDebuggerTabContent,
} from '../../pl-sql-debugger/types';

// Re-export debugger types — canonical definitions live in pl-sql-debugger/types.ts
export type PlSqlDebuggerObjectType = _PlSqlDebuggerObjectType;
export type PlSqlDebugState = _PlSqlDebugState;
export type ProgramIdentity = _ProgramIdentity;
export type PlSqlDebuggerTabContent = _PlSqlDebuggerTabContent;
export { createDefaultPlSqlDebuggerContent } from '../../pl-sql-debugger/types';

// ============================================================================
// PL/SQL Editor Types (canonical definitions in plsql-editor/types.ts)
// ============================================================================

import type {
  PlSqlEditorObjectType as _PlSqlEditorObjectType,
  PlSqlEditorTabContent as _PlSqlEditorTabContent,
  CompilationError as _CompilationError,
} from '../../plsql-editor/types';

export type PlSqlEditorObjectType = _PlSqlEditorObjectType;
export type PlSqlEditorTabContent = _PlSqlEditorTabContent;
export type CompilationError = _CompilationError;
export { createDefaultPlSqlEditorContent } from '../../plsql-editor/types';

/**
 * Session filter mode for the session browser
 */
export type SessionBrowserFilter = 'active' | 'all' | 'username' | 'program';

/**
 * Session row — engine-neutral superset.
 * Oracle maps: SID→sid, SERIAL#→serial, etc.
 * MSSQL maps: session_id→sid, 0→serial, login_name→username, etc.
 */
export type SessionBrowserRow = {
  sid: number;
  serial: number;
  username: string | null;
  status: string;
  osuser: string | null;
  machine: string | null;
  program: string | null;
  sqlId: string | null;
  event: string | null;
  waitClass: string | null;
  waitTime: number;
  secondsInWait: number;
  blockingSession: number | null;
  logonTime: string | null;
  /** Database name (MSSQL, PostgreSQL, MySQL) */
  database?: string | null;
  /** CPU time in ms (MSSQL) */
  cpuTime?: number;
  /** Memory usage in KB (MSSQL) */
  memoryUsage?: number;
  /** Logical reads (MSSQL) */
  logicalReads?: number;
  /** Physical reads (MSSQL) */
  reads?: number;
  /** Writes (MSSQL) */
  writes?: number;
  /** Current command (MSSQL) */
  command?: string | null;
  /** Backend start time (PostgreSQL) */
  backendStart?: string | null;
  /** Client address (PostgreSQL) */
  clientAddr?: string | null;
  /** Application name (PostgreSQL) */
  applicationName?: string | null;
};

/**
 * Session detail info (SQL, stats, waits, I/O)
 */
export type SessionBrowserDetail = {
  currentSql: string | null;
  sessionStats: { name: string; value: number }[];
  waitHistory: { event: string; waitTime: number; p1: string; p2: string; p3: string }[];
  sessionIo: { blockGets: number; consistentGets: number; physicalReads: number; blockChanges: number; consistentChanges: number } | null;
  /** Query plan XML (MSSQL) */
  queryPlan?: string | null;
};

/**
 * Detail panel tab in session browser
 */
export type SessionBrowserDetailTab = 'info' | 'cursors' | 'locks' | 'waits';

/**
 * Open cursor row from V$OPEN_CURSOR
 */
export type SessionBrowserCursorRow = {
  sqlId: string | null;
  sqlText: string | null;
  cursorType: string | null;
  lastSqlActiveTime: string | null;
};

/**
 * Lock row for the lock viewer
 */
export type SessionBrowserLockRow = {
  sid: number;
  serial: number;
  username: string | null;
  objectName: string | null;
  objectType: string | null;
  lockType: string;
  modeHeld: string;
  modeRequested: string;
  blockingSession: number | null;
};

/**
 * Cumulative lock-wait statistics row (PostgreSQL 19+ `pg_stat_lock`).
 * One row per lock type (relation, extend, frozenid, …) — a small fixed
 * row set, not per-session.
 */
export type SessionBrowserLockStatsRow = {
  lockType: string;
  waits: number;
  waitTime: number;
  fastpathExceeded: number;
  statsReset: string | null;
};

/**
 * Wait class summary.
 * `sessionCount` semantics vary by engine:
 * - Oracle/PostgreSQL: live session count (point-in-time snapshot)
 * - MSSQL: cumulative waiting_tasks_count from sys.dm_os_wait_stats
 * - MySQL: cumulative COUNT_STAR from performance_schema
 */
export type SessionBrowserWaitClass = {
  waitClass: string;
  sessionCount: number;
  totalSecondsInWait: number;
};

/**
 * Top wait event.
 * `sessionCount` semantics vary by engine — see SessionBrowserWaitClass.
 */
export type SessionBrowserTopWaitEvent = {
  event: string;
  waitClass: string;
  sessionCount: number;
  totalSecondsInWait: number;
};

/**
 * Content for session browser tabs
 */
export type SessionBrowserTabContent = {
  /** Current filter mode */
  filter: SessionBrowserFilter;
  /** Filter value (username or program name) */
  filterValue?: string;
  /** Cross-column search text */
  searchText?: string;
  /** Session rows */
  sessions?: SessionBrowserRow[];
  /** Selected session SID */
  selectedSid?: number;
  /** Selected session SERIAL# */
  selectedSerial?: number;
  /** Detail for the selected session */
  selectedDetail?: SessionBrowserDetail;
  /** Active detail panel tab */
  detailTab?: SessionBrowserDetailTab;
  /** Open cursors for selected session */
  cursors?: SessionBrowserCursorRow[];
  /** Locks across all sessions */
  locks?: SessionBrowserLockRow[];
  /** Wait class summary */
  waitClasses?: SessionBrowserWaitClass[];
  /** Top wait events */
  topWaitEvents?: SessionBrowserTopWaitEvent[];
  /** Auto-refresh interval in seconds (0 = off) */
  autoRefreshInterval: number;
  /** Loading state */
  isLoading?: boolean;
  /** Whether detail is loading */
  isDetailLoading?: boolean;
  /** Error message */
  error?: string;
};

// ============================================================================
// Job Scheduler Types (defined in job-scheduler/types/, re-exported here)
// ============================================================================

// Re-export all job scheduler domain types for backward compatibility
export {
  type JobSchedulerStatusFilter,
  type JobSchedulerCapabilities,
  type JobSchedulerActionPermissions,
  type JobRunStatus,
  type JobExecutionStatus,
  type JobSchedulerJobItem,
  type JobStepSubsystem,
  type StepAction,
  type JobSchedulerStepItem,
  type JobScheduleFreq,
  type JobSchedulerScheduleItem,
  type JobSchedulerNotifications,
  type JobSchedulerDetailData,
  type HistoryRunStatus,
  type HistoryStepResultItem,
  type HistoryRunItem,
  type HistoryDateRange,
  type HistorySummaryData,
  type JobSchedulerSubView,
  type AgentEditTarget,
  type JobSchedulerTabContent,
  createDefaultJobSchedulerContent,
} from '../../job-scheduler/types/jobSchedulerWorkspaceTypes';

// The following import makes the types available for use within this file
// (e.g., in JobSchedulerTab, isJobSchedulerTab, NewJobSchedulerTabInput)
import type { JobSchedulerTabContent } from '../../job-scheduler/types/jobSchedulerWorkspaceTypes';

// ── End of re-exported job scheduler types ──

/**
 * Tablespace row from DBA_TABLESPACES
 */
export type TablespaceManagerRow = {
  name: string;
  status: string;
  contents: string;
  blockSize: number;
  logging: string;
  extentManagement: string;
  segmentSpaceManagement: string;
  allocatedBytes: number;
  freeBytes: number;
  usedPercent: number;
  autoExtensible: boolean;
};

/**
 * Datafile row from DBA_DATA_FILES
 */
export type TablespaceManagerDatafile = {
  fileId: number;
  fileName: string;
  tablespaceName: string;
  bytes: number;
  maxBytes: number;
  autoExtensible: boolean;
  incrementBy: number;
  blockSize: number;
};

/**
 * I/O statistics for a datafile from V$FILESTAT / V$TEMPSTAT
 */
export type DatafileIOStatRow = {
  fileId: number;
  fileName: string;
  physicalReads: number;
  physicalWrites: number;
  readTime: number;
  writeTime: number;
  avgIOTime: number;
  singleBlockReads: number;
};

/**
 * Undo tablespace statistics from V$UNDOSTAT
 */
export type UndoStatRow = {
  beginTime: string;
  endTime: string;
  undoBlocks: number;
  txnCount: number;
  unexpiredBlocks: number;
  expiredBlocks: number;
};

/**
 * Undo tablespace monitoring data
 */
export type UndoInfoData = {
  undoTablespace: string;
  undoRetention: number;
  stats: UndoStatRow[];
};

/**
 * Content for tablespace manager tabs
 */
export type TablespaceManagerTabContent = {
  /** Tablespace rows */
  tablespaces?: TablespaceManagerRow[];
  /** Selected tablespace name */
  selectedTablespace?: string;
  /** Datafiles for selected tablespace */
  datafiles?: TablespaceManagerDatafile[];
  /** I/O statistics for selected tablespace datafiles */
  ioStats?: DatafileIOStatRow[];
  /** Whether I/O stats are loading */
  isIOStatsLoading?: boolean;
  /** Undo tablespace monitoring info */
  undoInfo?: UndoInfoData;
  /** Whether undo info is loading */
  isUndoLoading?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Whether datafiles are loading */
  isDatafilesLoading?: boolean;
  /** Error message */
  error?: string;
};

/**
 * Oracle object kind for the object detail viewer
 */
export type OracleDetailObjectKind = 'sequence' | 'synonym' | 'db-link' | 'materialized-view' | 'type';

/**
 * A key-value property for the object detail viewer
 */
export type OracleObjectProperty = {
  label: string;
  value: string | number | boolean | null;
};

/**
 * Type attribute (for type objects)
 */
export type OracleTypeAttribute = {
  name: string;
  dataType: string;
  attrNo: number;
};

/**
 * Type method (for type objects)
 */
export type OracleTypeMethod = {
  name: string;
  methodType: string;
  parameters: number;
  results: number;
};

/**
 * Content for Oracle object detail tabs
 */
export type OracleObjectDetailTabContent = {
  /** Object name */
  objectName: string;
  /** Schema (owner) */
  schema: string;
  /** Database */
  database: string;
  /** Object kind */
  objectKind: OracleDetailObjectKind;
  /** Key-value properties */
  properties?: OracleObjectProperty[];
  /** DDL source (for types, mviews) */
  source?: string;
  /** Type attributes */
  typeAttributes?: OracleTypeAttribute[];
  /** Type methods */
  typeMethods?: OracleTypeMethod[];
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string;
};

/**
 * Data Pump wizard mode
 */
export type DataPumpMode = 'export' | 'import' | 'monitor';

/**
 * Data Pump wizard step
 */
export type DataPumpWizardStep = 'prerequisites' | 'type' | 'objects' | 'options' | 'review';

/**
 * Data Pump job row (for monitor view)
 */
export type DataPumpJobRow = {
  jobName: string;
  ownerName: string;
  operation: string;
  jobMode: string;
  state: string;
  degree: number;
  attachedSessions: number;
};

/**
 * Content for Data Pump workspace tabs
 */
export type DataPumpTabContent = {
  /** Current mode: export wizard, import wizard, or job monitor */
  mode: DataPumpMode;
  /** Current wizard step (for export/import) */
  wizardStep?: DataPumpWizardStep;
  /** Prerequisites check result */
  hasExportRole?: boolean;
  hasImportRole?: boolean;
  /** Available directories */
  directories?: { name: string; path: string }[];
  /** Available schemas for export */
  availableSchemas?: string[];
  /** Available tables for selected schema */
  availableTables?: string[];
  /** Active Data Pump jobs */
  jobs?: DataPumpJobRow[];
  /** Generated script */
  generatedScript?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string;
};

/**
 * Content for Package Explorer tabs (Oracle only).
 *
 * Selection state (which package is currently being viewed) lives in
 * packagePinsStore.lastViewedByConnection, not on this content. This content
 * holds only fields that are specific to a given tab instance: the bound
 * connectionId, the snapshotted database, the search query, and the
 * persisted left-panel width.
 */
export type PackageExplorerTabContent = {
  /** The connection this Package Explorer is bound to (snapshotted at creation) */
  connectionId: string;
  /** The database/schema workspace name snapshotted at creation */
  database: string;
  /** Current search query for the left list */
  searchQuery?: string;
  /** Persisted splitter position in pixels */
  leftPanelWidthPx?: number;
};

// ============================================================================
// Connection Info (shared across workspace types)
// ============================================================================

export type TabConnectionInfo = {
  connectionId?: string;
  connectionName?: string;
  serverHost?: string;
  database?: string;
};

/**
 * Links a tab to the pinned workspace it was opened from. Set by
 * `workspaceStore.setSourcePin` when a tab is created via "Open" on a
 * Pinned Workspace entry (Object Explorer). Drives the diverged indicator
 * (comparing the tab's live content against the pin's snapshot) and the
 * "Update pin" action. Cleared via `workspaceStore.clearSourcePins` when the
 * pin is deleted (F11) or the tab is rebound to another connection (F2).
 */
export type TabSourcePin = {
  connectionId: string;
  pinId: string;
};

// ============================================================================
// Tab Types (discriminated union)
// ============================================================================

/**
 * Base tab fields shared across all workspace types
 */
type TabBase = {
  id: string;
  /** Display title for the tab */
  title: string;
  /** True if the user manually set the title (prevents auto-updates) */
  customTitle?: boolean;
  /** True if this tab is pinned (appears on the left, protected from bulk close) */
  isPinned?: boolean;
  connectionInfo?: TabConnectionInfo;
  /** Set when this tab was opened from a Pinned Workspace entry; see `TabSourcePin`. */
  sourcePin?: TabSourcePin;
};

/**
 * Query editor tab
 */
export type QueryTab = TabBase & {
  type: 'query';
  content: QueryTabContent;
  /** Unique execution ID for the current/last query execution */
  executionId?: string;
};

/**
 * Table editor tab (Phase 9)
 */
export type TableEditorTab = TabBase & {
  type: 'table-editor';
  content: TableEditorTabContent;
};

/**
 * Table explorer tab (Phase 10)
 */
export type TableExplorerTab = TabBase & {
  type: 'table-explorer';
  content: TableExplorerTabContent;
};

/**
 * Execution plan tab (imported plan files + compare)
 */
export type ExecutionPlanTab = TabBase & {
  type: 'plan';
  content: ExecutionPlanTabContent;
};

/**
 * Table designer tab (create/alter tables via GUI)
 */
export type TableDesignerTab = TabBase & {
  type: 'table-designer';
  content: TableDesignerTabContent;
};

/**
 * Security manager tab (manage logins, users, roles, schemas)
 */
export type SecurityManagerTab = TabBase & {
  type: 'security';
  content: SecurityManagerTabContent;
};

/**
 * Schema compare tab (compare database schemas)
 */
export type SchemaCompareTab = TabBase & {
  type: 'schema-compare';
  content: SchemaCompareTabContent;
};

/**
 * Database Blueprint tab — folder-backed DDL export + bidirectional sync.
 * See `src/features/database-blueprint/` and `Docs/DatabaseBlueprint.md`.
 */
export type DatabaseBlueprintTabContent = {
  /** MountedFolder.id — links the tab to the Scripts-panel blueprint folder. */
  blueprintFolderId: string;
  /** Optional: which file is being previewed in the peek viewer. */
  selectedFilePath?: string;
  /** Optional: which linked DB the Connections panel has selected. */
  selectedLinkedDbLabel?: string;
  /** Active panel tab inside the DatabaseBlueprint workspace. */
  activePanel?: 'files' | 'git' | 'drift';
};

export type DatabaseBlueprintTab = TabBase & {
  type: 'database-blueprint';
  content: DatabaseBlueprintTabContent;
};

/**
 * Cross-engine migration tab — copies a single table OR every table in a source
 * database to a target connection of any engine, streaming the data in chunks
 * with live progress + cancel. Only the CONFIG (source/target/subset) is
 * persisted; the in-flight run lives in the per-tab `migrationRunStore` and is
 * not restored (a migration can't be resumed mid-stream).
 * See `src/features/cross-engine-migrate/` and `Docs/...`.
 */
export type CrossEngineMigrateTabContent = {
  /** Single table or every table in the source database. */
  scope: 'table' | 'database';
  /** Source descriptor (schema/table are set only for `scope: 'table'`). */
  source: {
    connectionId: string;
    database: string;
    engine: string;
    schema?: string;
    table?: string;
  };
  /** Chosen target connection + database — filled during the config phase. */
  target?: { connectionId: string; database: string; engine: string };
  /** database scope only: null/undefined = every table, else the chosen subset. */
  subset?: Array<{ schema: string; table: string }> | null;
};

export type CrossEngineMigrateTab = TabBase & {
  type: 'cross-engine-migrate';
  content: CrossEngineMigrateTabContent;
};

/**
 * Data Import Wizard step. Persisted so a restored tab reopens on the same
 * step, subject to the gate-fallback guard applied in `tabDeserializer`
 * (never restores to a step whose prerequisites aren't met).
 */
export type DataImportStep = 'source' | 'target' | 'columns' | 'import';

/**
 * A user edit to one column's plan, keyed by `sourceName` (NOT the analysis
 * `index`) so it survives both a re-analysis of the SAME file (revision
 * refinement) and "Import another file" (a new file whose headers happen to
 * match). Only the touched fields are set — everything else (canonical type,
 * stats, index) always comes fresh from the latest analysis.
 *
 * `nullable`/`inPrimaryKey` (S4.T3b): user schema-design edits made in the
 * Columns editor popover — they PERSIST like every other override here (the
 * user is designing the new table's shape, not just previewing it).
 */
export type DataImportColumnOverride = {
  sourceName: string;
  include?: boolean;
  name?: string;
  nativeType?: string;
  dateFormat?: 'ISO' | 'DMY' | 'MDY';
  nullable?: boolean;
  inPrimaryKey?: boolean;
};

/**
 * Data Import Wizard tab content — CONFIG ONLY. The live analysis
 * (`ImportAnalysis`, revision-stamped) and the merged column plan live in the
 * per-tab `dataImportStore`, never here — match counts, run progress, and
 * post-success suggestion state NEVER persist. See `Docs/DataImport-Plan.md`.
 */
export type DataImportTabContent = {
  /** Source file path; undefined until chosen (or cleared by "Import another file"). */
  filePath?: string;
  /** User-overridden parse options; undefined fields fall back to auto-detection. */
  parse?: Partial<ImportParseOptions>;
  /** Chosen destination (new or existing table); undefined until the Target step is completed. */
  target?: ImportTargetPlan;
  /** Open-time Target prefill (Object Explorer "Import Data…" on a database, toolbar, palette):
   * seeds the DRAFT destination once, at entry creation, and only when no committed `target`
   * exists. Never written back by the store mirror — live drafts stay ephemeral (Rev 10). */
  draftTarget?: Partial<ImportTargetPlan>;
  /** Current wizard step. */
  step: DataImportStep;
  /** Column edits (include/name/type/date-format), keyed by sourceName. */
  columnOverrides?: DataImportColumnOverride[];
  /** Existing-table mode target mappings, including fixed-value mappings. */
  mappings?: ImportTargetMapping[];
  /** File-local row filter (scope + conditions); the runner only honors scope: 'import'. */
  rowFilter?: ImportRowFilter;
};

export type DataImportTab = TabBase & {
  type: 'data-import';
  content: DataImportTabContent;
};

/** Where a Deadlock Analyzer tab's report came from. */
export type DeadlockAnalyzerSourceKind = 'file' | 'paste' | 'sample' | 'connection' | 'none';

/** Collector time filter (F1-B); custom boundaries are ISO-8601 UTC instants. */
export type DeadlockCollectorRangeFilter =
  | { kind: 'last24h' }
  | { kind: 'last7d' }
  | { kind: 'custom'; fromUtc: string; toUtc: string };

/**
 * Deadlock Analyzer tab content — SOURCE METADATA ONLY. The imported XML, the
 * parsed report and the parse state live in the per-tab `deadlockAnalyzerStore`
 * (memory) and are never persisted; a restored `paste` tab asks for the report
 * again. `connectionId` is an optional hint for the future live collector.
 */
export type DeadlockAnalyzerTabContent = {
  sourceKind: DeadlockAnalyzerSourceKind;
  /** Absolute path of the opened .xdl/.xml file (desktop only). */
  filePath?: string;
  fileName?: string;
  selectedIncidentIndex?: number;
  selectedNodeId?: string;
  /** Connection read by the collector (`sourceKind: 'connection'`); a hint otherwise. */
  connectionId?: string;
  /** Collector filters (F1-B) — persisted so a restored tab keeps them; results are never persisted. */
  collectorRange?: DeadlockCollectorRangeFilter;
  /** Database-name filter; matches an incident when ANY participant belongs to that database. */
  collectorDatabase?: string;
};

export type DeadlockAnalyzerTab = TabBase & {
  type: 'deadlock-analyzer';
  content: DeadlockAnalyzerTabContent;
};

/** Column sort persisted for the Query Store compare table. */
export type QueryStoreTableSort = { id: string; desc: boolean };

/**
 * Query Store tab content — IDENTITY AND FILTER PREFERENCES ONLY. The state
 * header, compare rows, query text, per-interval rows and plans are fetched
 * through TanStack Query and never persisted; `selectedQueryId` is a catalog
 * id (decimal string), not text. The connection lives in `connectionInfo`.
 */
export type QueryStoreTabContent = {
  database: string;
  /** Length of each comparison period in hours (recent and baseline are equal). */
  periodHours: number;
  /** ISO-8601 UTC instant the recent period ends at; undefined = now. */
  anchorUtc?: string;
  includeActiveInterval: boolean;
  metric: QueryStoreMetric;
  /** Minimum regular executions in EACH window before a row is `compared`. */
  minExecutions: number;
  /** Bounded result size (grows with "Show more"). */
  limit: number;
  sort?: QueryStoreTableSort;
  selectedQueryId?: string;
};

export type QueryStoreTab = TabBase & {
  type: 'query-store';
  content: QueryStoreTabContent;
};

// ============================================================================
// Storage Explorer (Azure Storage engine, spec §6.1)
// ============================================================================

/** Derived from `root.kind`; stored for fast dispatch and recomputed on restore. */
export type StorageExplorerMode = 'blobs' | 'entities' | 'messages' | 'files' | 'hns';

export type StorageExplorerListing = 'hierarchy' | 'flat';

export type StorageExplorerView = {
  listing: StorageExplorerListing;
  prefixFilter: string;
  showSnapshots: boolean;
  showVersions: boolean;
  showDeleted: boolean;
};

/** Table-entity view configuration (S4 fills it; persisted from S1 so the shape never migrates). */
export type StorageExplorerEntityView = {
  filters: FilterGroup[];
  odataFilter?: string;
  filterMode: 'wizard' | 'odata';
  selectedColumns: string[] | '*';
  pageSize: number;
};

/** Queue-message view configuration (S5). */
export type StorageExplorerMessageView = {
  peekCount: number;
  decodeBase64: boolean;
};

/**
 * Storage Explorer tab content — IDENTITY AND VIEW CONFIGURATION ONLY.
 * `SerializedStorageExplorerTabContent` persists exactly `{ connectionId,
 * root, resource, mode, view, entityView?, messageView? }`. Listing pages live
 * in TanStack Query (`useStorageListing`), and history / selection / request
 * bookkeeping in the per-tab `storageExplorerStore` — never here, so a page
 * append never re-renders every workspace subscriber (the deadlock-analyzer
 * precedent). `openToken` / `restoredFromSession` are runtime-only.
 */
export type StorageExplorerTabContent = {
  connectionId: string;
  /** container | table | queue | share | hnsPath(dir) | blob (single-blob attachment); navigation never leaves the root. */
  root: StorageResourceRef;
  /** Current location (blobPrefix / directory / hnsPath under root; equals root for entities / messages). */
  resource: StorageResourceRef;
  mode: StorageExplorerMode;
  view: StorageExplorerView;
  entityView?: StorageExplorerEntityView;
  messageView?: StorageExplorerMessageView;
  /** Perf-mark correlation key set by `openStorageExplorerTab` (spec §6.3); absent after restore; never persisted. */
  openToken?: string;
  /** Set by the session deserializer so the tab fires `feature:storage_browse_open { source: 'restore' }`; never persisted. */
  restoredFromSession?: boolean;
};

export type StorageExplorerTab = TabBase & {
  type: 'storage-explorer';
  content: StorageExplorerTabContent;
};

/**
 * Data compare tab (compare table data)
 */
export type DataCompareTab = TabBase & {
  type: 'data-compare';
  content: DataCompareTabContent;
};

/**
 * Dependency viewer tab (show object dependencies)
 */
export type DependencyViewerTab = TabBase & {
  type: 'dependency-viewer';
  content: DependencyViewerTabContent;
};

/**
 * Schema overview tab (visualize database schema as graph)
 */
export type SchemaOverviewTab = TabBase & {
  type: 'schema-overview';
  content: SchemaOverviewTabContent;
};

/**
 * Spatial Explorer tab (view spatial layers on a shared map)
 */
export type SpatialExplorerTab = TabBase & {
  type: 'spatial-explorer';
  content: SpatialExplorerTabContent;
};

/**
 * Data profile tab (per-column histogram, top-N, summary stats)
 */
export type DataProfileTab = TabBase & {
  type: 'data-profile';
  content: DataProfileTabContent;
};

/**
 * Start page tab (welcome/dashboard screen)
 */
export type StartPageTab = TabBase & {
  type: 'start-page';
  content: StartPageTabContent;
};

/**
 * SQL Notebook tab (Jupyter-style cells)
 */
export type NotebookTab = TabBase & {
  type: 'notebook';
  content: NotebookTabContent;
};

/**
 * Oracle Session Browser tab
 */
export type SessionBrowserTab = TabBase & {
  type: 'session-browser';
  content: SessionBrowserTabContent;
};

/**
 * Oracle Tablespace Manager tab
 */
export type TablespaceManagerTab = TabBase & {
  type: 'tablespace-manager';
  content: TablespaceManagerTabContent;
};

/**
 * Oracle Object Detail tab
 */
export type OracleObjectDetailTab = TabBase & {
  type: 'oracle-object-detail';
  content: OracleObjectDetailTabContent;
};

/**
 * Oracle Data Pump tab
 */
export type DataPumpTab = TabBase & {
  type: 'data-pump';
  content: DataPumpTabContent;
};

/**
 * Instance Monitor / Performance Dashboard tab content
 */
export type InstanceMonitorActivePanel = 'dashboard' | 'top-sql' | 'alert-log' | 'awr' | 'wait-stats' | 'autovacuum';

export type InstanceMonitorAutovacuumRow = {
  tableName: string;
  schemaName: string;
  score: number;
  xidScore: number;
  mxidScore: number;
  vacuumScore: number;
  vacuumInsertScore: number;
  analyzeScore: number;
  doVacuum: boolean;
  doAnalyze: boolean;
  forWraparound: boolean;
};

export type InstanceMonitorTabContent = {
  activePanel: InstanceMonitorActivePanel;
  autoRefreshInterval: number;
  isLoading?: boolean;
  error?: string;
  // Engine capabilities (determines which panels are visible)
  capabilities?: {
    hasAwrSupport: boolean;
    hasErrorLog: boolean;
    hasWaitStats: boolean;
    /** PostgreSQL 19+ only: `pg_stat_autovacuum_scores` is queryable. */
    hasAutovacuumScores?: boolean;
  };
  // Dashboard data (engine-neutral)
  instanceInfo?: {
    serverName: string;
    hostName: string;
    version: string;
    status: string;
    startupTime: string;
    platform: string;
    properties: { name: string; value: string }[];
  };
  memoryStats?: { name: string; bytes: number; category: string }[];
  // System stats
  systemStats?: { name: string; value: number; unit?: string }[];
  bufferCacheHitRatio?: number;
  // Top SQL
  topSqlOrderBy?: 'elapsed_time' | 'cpu_time' | 'disk_reads' | 'buffer_gets';
  topSqlRows?: {
    sqlId: string;
    sqlText: string;
    executions: number;
    elapsedTime: number;
    cpuTime: number;
    diskReads: number;
    bufferGets: number;
    rowsProcessed: number;
    elapsedPerExec: number;
  }[];
  isTopSqlLoading?: boolean;
  // Alert / Error log
  alertLogEntries?: { messageText: string; messageType: number; timestamp: string }[];
  alertLogSeverity?: 'all' | 'error' | 'warning';
  isAlertLogLoading?: boolean;
  // AWR
  awrLicensed?: boolean;
  awrSnapshots?: { snapId: number; beginTime: string; endTime: string }[];
  awrReportHtml?: string;
  isAwrLoading?: boolean;
  // Wait Stats
  waitStats?: { waitType: string; category: string; waitCount: number; waitTimeMs: number }[];
  isWaitStatsLoading?: boolean;
  // Autovacuum (PostgreSQL 19+)
  autovacuumRows?: InstanceMonitorAutovacuumRow[];
  autovacuumUnavailableReason?: 'requires-pg19';
  isAutovacuumLoading?: boolean;
};

/**
 * Oracle Instance Monitor tab
 */
export type InstanceMonitorTab = TabBase & {
  type: 'instance-monitor';
  content: InstanceMonitorTabContent;
};

/**
 * Content for filegroup manager tabs (MSSQL-only)
 */
export type FilegroupManagerTabContent = {
  /** Filegroup rows */
  filegroups?: FilegroupManagerRow[];
  /** Transaction log files */
  logFiles?: FilegroupManagerFileRow[];
  /** Selected filegroup name */
  selectedFilegroup?: string;
  /** Database files for selected filegroup */
  files?: FilegroupManagerFileRow[];
  /** I/O statistics for selected filegroup files */
  ioStats?: FilegroupManagerIoStats[];
  /** Loading state */
  isLoading?: boolean;
  /** Whether files are loading */
  isFilesLoading?: boolean;
  /** Error message */
  error?: string;
  /** Auto-refresh interval in seconds (0 = off) */
  autoRefreshInterval?: number;
};

export type FilegroupManagerRow = {
  name: string;
  type: string;
  isDefault: boolean;
  isReadOnly: boolean;
  fileCount: number;
  totalSizeMb: number;
  usedSizeMb: number;
  freeSizeMb: number;
  usedPercent: number;
};

export type FilegroupManagerFileRow = {
  fileId: number;
  name: string;
  physicalName: string;
  typeDesc: string;
  filegroupName: string | null;
  sizeMb: number;
  maxSizeMb: number;
  growthDesc: string;
  spaceUsedMb: number;
  freeSpaceMb: number;
  usedPercent: number;
  isAutoGrowth: boolean;
  isPercentGrowth: boolean;
};

export type FilegroupManagerIoStats = {
  fileId: number;
  name: string;
  numOfReads: number;
  numOfWrites: number;
  numOfBytesRead: number;
  numOfBytesWritten: number;
  ioStallReadMs: number;
  ioStallWriteMs: number;
};

/**
 * MSSQL Filegroup Manager tab
 */
export type FilegroupManagerTab = TabBase & {
  type: 'filegroup-manager';
  content: FilegroupManagerTabContent;
};

/**
 * PL/SQL Debugger tab
 */
export type PlSqlDebuggerTab = TabBase & {
  type: 'pl-sql-debugger';
  content: PlSqlDebuggerTabContent;
};

/**
 * Job Scheduler tab
 */
export type JobSchedulerTab = TabBase & {
  type: 'job-scheduler';
  content: JobSchedulerTabContent;
};

/**
 * PL/SQL Editor tab (unified edit + debug)
 */
export type PlSqlEditorTab = TabBase & {
  type: 'plsql-editor';
  content: PlSqlEditorTabContent;
};

/**
 * Package Explorer tab (Oracle only)
 */
export type PackageExplorerTab = TabBase & {
  type: 'package-explorer';
  content: PackageExplorerTabContent;
};

/**
 * Dashboard tab content (dashboards feature, S2).
 * Holds only the pointer into the main-process DashboardRepository
 * ({userData}/dashboards) — definitions live there, tile results live in the
 * TanStack Query cache. Nothing else is persisted per tab.
 */
export type DashboardTabContent = {
  /** Persisted dashboard id shown by this tab; null → zero state (or most-recent on open). */
  dashboardId: string | null;
};

/**
 * Dashboard workspace tab
 */
export type DashboardTab = TabBase & {
  type: 'dashboard';
  content: DashboardTabContent;
};

/**
 * Find-references results tab (NavigationalFeatures Phase 3 — Slice 24).
 *
 * Lists every persisted object that references the chosen identifier (column,
 * table, view, routine). The content type lives in the navigational-features
 * feature folder so the workspace registry imports across the boundary
 * without duplicating the shape.
 */
export type ReferencesTab = TabBase & {
  type: 'references-tab';
  content: ReferencesTabContent;
};

/**
 * Union of all tab types
 */
export type Tab = QueryTab | TableEditorTab | TableExplorerTab | ExecutionPlanTab | TableDesignerTab | SecurityManagerTab | SchemaCompareTab | DataCompareTab | DependencyViewerTab | SchemaOverviewTab | SpatialExplorerTab | DataProfileTab | StartPageTab | NotebookTab | SessionBrowserTab | TablespaceManagerTab | OracleObjectDetailTab | DataPumpTab | InstanceMonitorTab | FilegroupManagerTab | PlSqlDebuggerTab | JobSchedulerTab | PlSqlEditorTab | PackageExplorerTab | ReferencesTab | DatabaseBlueprintTab | CrossEngineMigrateTab | DashboardTab | DataImportTab | DeadlockAnalyzerTab | QueryStoreTab | StorageExplorerTab;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard for query tabs
 */
export function isQueryTab(tab: Tab): tab is QueryTab {
  return tab.type === 'query';
}

/**
 * Type guard for table editor tabs
 */
export function isTableEditorTab(tab: Tab): tab is TableEditorTab {
  return tab.type === 'table-editor';
}

/**
 * Type guard for table explorer tabs
 */
export function isTableExplorerTab(tab: Tab): tab is TableExplorerTab {
  return tab.type === 'table-explorer';
}

/**
 * Type guard for execution plan tabs
 */
export function isExecutionPlanTab(tab: Tab): tab is ExecutionPlanTab {
  return tab.type === 'plan';
}

/**
 * Type guard for table designer tabs
 */
export function isTableDesignerTab(tab: Tab): tab is TableDesignerTab {
  return tab.type === 'table-designer';
}

/**
 * Type guard for security manager tabs
 */
export function isSecurityManagerTab(tab: Tab): tab is SecurityManagerTab {
  return tab.type === 'security';
}

/**
 * Type guard for schema compare tabs
 */
export function isSchemaCompareTab(tab: Tab): tab is SchemaCompareTab {
  return tab.type === 'schema-compare';
}

/**
 * Type guard for database blueprint tabs
 */
export function isDatabaseBlueprintTab(tab: Tab): tab is DatabaseBlueprintTab {
  return tab.type === 'database-blueprint';
}

/**
 * Type guard for cross-engine migration tabs
 */
export function isCrossEngineMigrateTab(tab: Tab): tab is CrossEngineMigrateTab {
  return tab.type === 'cross-engine-migrate';
}

/**
 * Type guard for deadlock analyzer tabs
 */
export function isDeadlockAnalyzerTab(tab: Tab): tab is DeadlockAnalyzerTab {
  return tab.type === 'deadlock-analyzer';
}

/** Default content for a deadlock analyzer tab — nothing imported yet. */
export function createDefaultDeadlockAnalyzerContent(): DeadlockAnalyzerTabContent {
  return { sourceKind: 'none' };
}

/**
 * Type guard for Query Store tabs
 */
export function isQueryStoreTab(tab: Tab): tab is QueryStoreTab {
  return tab.type === 'query-store';
}

/**
 * Type guard for Storage Explorer tabs
 */
export function isStorageExplorerTab(tab: Tab): tab is StorageExplorerTab {
  return tab.type === 'storage-explorer';
}

/** The tab mode a resource ref opens in (spec §6.2). Account / service refs have no browser mode; they fall back to blobs. */
export function storageExplorerModeFor(ref: StorageResourceRef): StorageExplorerMode {
  switch (ref.kind) {
    case 'table':
    case 'entity':
      return 'entities';
    case 'queue':
    case 'message':
      return 'messages';
    case 'share':
    case 'directory':
    case 'file':
      return 'files';
    case 'hnsPath':
      return 'hns';
    case 'account':
    case 'service':
    case 'container':
    case 'blobPrefix':
    case 'blob':
      return 'blobs';
  }
}

export function createDefaultStorageExplorerView(): StorageExplorerView {
  return { listing: 'hierarchy', prefixFilter: '', showSnapshots: false, showVersions: false, showDeleted: false };
}

/**
 * Default content for a Storage Explorer tab. An account root never lists in
 * S1 (the browser needs a container / table / queue / share root); callers go
 * through `openStorageExplorerTab`, which always supplies the identity fields.
 */
export function createDefaultStorageExplorerContent(): StorageExplorerTabContent {
  const root: StorageResourceRef = { kind: 'account' };
  return { connectionId: '', root, resource: root, mode: 'blobs', view: createDefaultStorageExplorerView() };
}

/** Default content for a Query Store tab — last 24h vs the preceding 24h, duration, min 5 executions. */
export function createDefaultQueryStoreContent(database = ''): QueryStoreTabContent {
  return {
    database,
    periodHours: QUERY_STORE_DEFAULT_PERIOD_HOURS,
    includeActiveInterval: false,
    metric: 'duration',
    minExecutions: QUERY_STORE_DEFAULT_MIN_EXECUTIONS,
    limit: QUERY_STORE_DEFAULT_LIMIT,
  };
}

/**
 * Type guard for data import wizard tabs
 */
export function isDataImportTab(tab: Tab): tab is DataImportTab {
  return tab.type === 'data-import';
}

/**
 * Create default content for a database-blueprint tab.
 */
export function createDefaultDatabaseBlueprintContent(
  blueprintFolderId: string
): DatabaseBlueprintTabContent {
  return {
    blueprintFolderId,
    activePanel: 'files',
  };
}

/**
 * Default (skeleton) content for a cross-engine migration tab. The opener always
 * supplies a concrete `content` (merged over this), so this only guards
 * `createDefaultContent`'s exhaustiveness.
 */
export function createDefaultCrossEngineMigrateContent(): CrossEngineMigrateTabContent {
  return {
    scope: 'table',
    source: { connectionId: '', database: '', engine: 'mssql' },
    subset: null,
  };
}

/**
 * Default (skeleton) content for a data import tab — starts on the source step
 * with nothing selected yet.
 */
export function createDefaultDataImportContent(): DataImportTabContent {
  return {
    step: 'source',
  };
}

/**
 * Type guard for data compare tabs
 */
export function isDataCompareTab(tab: Tab): tab is DataCompareTab {
  return tab.type === 'data-compare';
}

/**
 * Type guard for dependency viewer tabs
 */
export function isDependencyViewerTab(tab: Tab): tab is DependencyViewerTab {
  return tab.type === 'dependency-viewer';
}

/**
 * Type guard for schema overview tabs
 */
export function isSchemaOverviewTab(tab: Tab): tab is SchemaOverviewTab {
  return tab.type === 'schema-overview';
}

/**
 * Type guard for spatial explorer tabs
 */
export function isSpatialExplorerTab(tab: Tab): tab is SpatialExplorerTab {
  return tab.type === 'spatial-explorer';
}

/**
 * Type guard for data profile tabs
 */
export function isDataProfileTab(tab: Tab): tab is DataProfileTab {
  return tab.type === 'data-profile';
}

/**
 * Type guard for start page tabs
 */
export function isStartPageTab(tab: Tab): tab is StartPageTab {
  return tab.type === 'start-page';
}

/**
 * Type guard for notebook tabs
 */
export function isNotebookTab(tab: Tab): tab is NotebookTab {
  return tab.type === 'notebook';
}

/**
 * Type guard for session browser tabs
 */
export function isSessionBrowserTab(tab: Tab): tab is SessionBrowserTab {
  return tab.type === 'session-browser';
}

/**
 * Type guard for tablespace manager tabs
 */
export function isTablespaceManagerTab(tab: Tab): tab is TablespaceManagerTab {
  return tab.type === 'tablespace-manager';
}

/**
 * Type guard for Oracle object detail tabs
 */
export function isOracleObjectDetailTab(tab: Tab): tab is OracleObjectDetailTab {
  return tab.type === 'oracle-object-detail';
}

/**
 * Type guard for Data Pump tabs
 */
export function isDataPumpTab(tab: Tab): tab is DataPumpTab {
  return tab.type === 'data-pump';
}

/**
 * Type guard for Instance Monitor tabs
 */
export function isInstanceMonitorTab(tab: Tab): tab is InstanceMonitorTab {
  return tab.type === 'instance-monitor';
}

/**
 * Type guard for Filegroup Manager tabs
 */
export function isFilegroupManagerTab(tab: Tab): tab is FilegroupManagerTab {
  return tab.type === 'filegroup-manager';
}

/**
 * Type guard for PL/SQL debugger tabs
 */
export function isPlSqlDebuggerTab(tab: Tab): tab is PlSqlDebuggerTab {
  return tab.type === 'pl-sql-debugger';
}

/**
 * Type guard for job scheduler tabs
 */
export function isJobSchedulerTab(tab: Tab): tab is JobSchedulerTab {
  return tab.type === 'job-scheduler';
}

/**
 * Type guard for PL/SQL editor tabs
 */
export function isPlSqlEditorTab(tab: Tab): tab is PlSqlEditorTab {
  return tab.type === 'plsql-editor';
}

/**
 * Type guard for package explorer tabs
 */
export function isPackageExplorerTab(tab: Tab): tab is PackageExplorerTab {
  return tab.type === 'package-explorer';
}

/**
 * Type guard for dashboard tabs
 */
export function isDashboardTab(tab: Tab): tab is DashboardTab {
  return tab.type === 'dashboard';
}

/**
 * Type guard for the find-references results tab.
 */
export function isReferencesTab(tab: Tab): tab is ReferencesTab {
  return tab.type === 'references-tab';
}

// ============================================================================
// Default Content Factories
// ============================================================================

/**
 * Create default content for a query tab
 */
export function createDefaultQueryContent(): QueryTabContent {
  return {
    query: '',
    includeActualPlan: false,
    resultSets: [],
    messages: [],
    results: [],
    columns: [],
    executionPlan: undefined,
    transactionMode: 'auto',
    transactionState: 'idle',
    isolationLevel: 'default',
  };
}

/**
 * Create default content for a table editor tab
 */
export function createDefaultTableEditorContent(
  tableName: string,
  schema: string = 'dbo'
): TableEditorTabContent {
  return {
    tableName,
    schema,
    filters: [],
    pendingChanges: [],
    rows: [],
    columns: [],
  };
}

/**
 * Create default content for a table explorer tab
 */
export function createDefaultTableExplorerContent(
  tableName: string,
  schema: string = 'dbo',
  database: string = ''
): TableExplorerTabContent {
  return {
    tableName,
    schema,
    database,
    rows: [],
    columns: [],
    limit: 100,
    selectedColumns: '*',
    filters: [],
    sorting: [],
    isLoading: false,
    isEditMode: false,
    pendingChanges: [],
    primaryKeyColumns: [],
  };
}

/**
 * Create default content for a table designer tab
 */
export function createDefaultTableDesignerContent(
  mode: TableDesignerMode,
  database: string = '',
  schema: string = 'dbo',
  tableName: string = ''
): TableDesignerTabContent {
  return {
    mode,
    tableName,
    schema,
    database,
    columns: [],
    isDirty: false,
    isLoading: false,
    isApplying: false,
  };
}

/**
 * Create default content for a security manager tab
 */
export function createDefaultSecurityManagerContent(
  view: SecurityManagerView = 'server',
  objectType: SecurityObjectType = 'login',
  selectedDatabase?: string
): SecurityManagerTabContent {
  return {
    view,
    objectType,
    selectedDatabase,
    isLoading: false,
    isDirty: false,
    isApplying: false,
  };
}

/**
 * Create default content for a schema compare tab.
 *
 * W5.1: when `engine` is supplied and the user has saved defaults for it
 * (Schema Compare setup dialog's "Save as my defaults"), those override the
 * hardcoded baseline below via `getSchemaCompareDefaultOptions`. `engine` is
 * optional and backward-compatible — callers that can't derive it (no
 * connection picked yet) get exactly today's hardcoded baseline.
 *
 * W5.2 (D3): the baseline itself now sets `ignoredProperties` to the Standard
 * depth preset's per-engine volatile-property list (alongside the pre-existing
 * `detailed: true`). Leaving `ignoredProperties` unset made `inferDepth`
 * silently read this baseline as the Deep preset (full fetch, nothing
 * pre-ignored) — "every fresh compare starts at Deep" — even though the docs
 * always claimed Standard. A saved default's own `ignoredProperties` (spread
 * after) still wins, so this changes nothing for anyone who already saved one.
 *
 * W5.2 fix round 1 (F-3): the ignore list is written ONLY when `engine` is
 * known. With no engine, `getStandardIgnoredProperties(undefined)` returns a
 * cross-engine union (`fillFactor`, `autoIncrement`, `pctFree`, `iniTrans`)
 * that matches no real engine's Standard list — so the moment the tab resolved
 * its actual engine, `inferDepth` reported **'custom'** (a preset the user
 * never picked) over a half-Standard suppression set that pre-ignored
 * `fillFactor` but not `identitySeed`/`identityIncrement`/`dataCompression`.
 * Writing no list at all instead leaves an honest "nothing pre-ignored"
 * baseline that reads back as a real preset on every engine, and every caller
 * that CAN derive an engine now does (`workspaceStore.createDefaultContent`,
 * `tabDeserializer`), so this path is only reached when the connection itself
 * can't be resolved.
 */
export function createDefaultSchemaCompareContent(
  source?: SchemaCompareEndpoint,
  target?: SchemaCompareEndpoint,
  engine?: DatabaseEngineId
): SchemaCompareTabContent {
  return {
    source,
    target,
    options: {
      ignoreCase: false,
      ignoreWhitespace: false,
      includeTables: true,
      includeViews: true,
      includeProcedures: true,
      includeFunctions: true,
      // Behaviourally identical to leaving it undefined (every consumer treats
      // an absent flag as included), but stated so the six object types read
      // the same way here as they do everywhere else.
      includeSequences: true,
      includeUserTypes: true,
      detailed: true,
      ...(engine ? { ignoredProperties: getStandardIgnoredProperties(engine) } : {}),
      ...getSchemaCompareDefaultOptions(engine),
    },
    compareWholeDatabase: true,
    hasCompared: false,
    isComparing: false,
    needsRefresh: false,
  };
}

/**
 * Create default content for a data compare tab
 */
export function createDefaultDataCompareContent(
  source?: DataCompareEndpoint,
  target?: DataCompareEndpoint
): DataCompareTabContent {
  return {
    source,
    target,
    keyMappings: [],
    valueMappings: [],
    options: {
      caseSensitive: false,
      trimWhitespace: true,
      nullEqualsEmpty: false,
      numericPrecision: undefined,
      dateTimePrecision: 'exact',
    },
    hasCompared: false,
    isComparing: false,
    needsRefresh: false,
  };
}

/**
 * Create default content for a dependency viewer tab
 */
export function createDefaultDependencyViewerContent(
  objectName: string,
  objectType: DependencyObjectType,
  schema: string,
  database: string
): DependencyViewerTabContent {
  return {
    objectName,
    objectType,
    schema,
    database,
    viewMode: 'tree',
    isLoading: true,
    navigationStack: [],
  };
}

/**
 * Create default content for a schema overview tab
 */
export function createDefaultSchemaOverviewContent(
  connectionId: string,
  databaseName: string,
  filterMode?: SchemaFilterMode
): SchemaOverviewTabContent {
  return {
    connectionId,
    databaseName,
    filterMode: filterMode ?? 'all',
    isLoading: true,
  };
}

/**
 * Create default content for a spatial explorer tab — an empty (zero-layer) view.
 */
export function createDefaultSpatialExplorerContent(
  connectionId: string,
  databaseName: string
): SpatialExplorerTabContent {
  return {
    connectionId,
    databaseName,
    view: { layers: [], basemap: 'default' },
  };
}

/**
 * Create default content for a data profile tab
 */
export function createDefaultDataProfileContent(params: {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  focusedColumn?: string;
  dataMode?: DataProfileDataMode;
}): DataProfileTabContent {
  return {
    connectionId: params.connectionId,
    database: params.database,
    schema: params.schema,
    table: params.table,
    focusedColumn: params.focusedColumn,
    dataMode: params.dataMode ?? 'server',
    sampleSize: 10000,
    bucketCount: 8,
    histogramMode: 'equal-width',
    histogramAxis: 'auto',
    histogramYScale: 'linear',
    topN: 10,
    isLoading: false,
  };
}

/**
 * Create default content for a start page tab
 */
export function createDefaultStartPageContent(): StartPageTabContent {
  return {
    expandedSections: {
      connections: true,
      recentTabs: true,
      queryHistory: true,
    },
    tabSearchQuery: '',
    selectedConnectionId: null,
  };
}

/**
 * Create default content for a dashboard tab
 */
export function createDefaultDashboardContent(): DashboardTabContent {
  return {
    dashboardId: null,
  };
}

/**
 * Create empty default content for a find-references tab. Callers always
 * override the relevant fields (connectionId, identifier, kind, groups); this
 * factory exists so the workspace store's `addTab` can fall back to a
 * well-formed shape when the input is partial.
 */
export function createDefaultReferencesContent(): ReferencesTabContent {
  return {
    connectionId: '',
    database: '',
    engineId: 'mssql',
    identifier: { name: '', raw: '', quoted: false, foldedName: '' },
    identifierKind: 'column',
    groups: [],
    bodies: {},
    status: 'idle',
  };
}

/**
 * Create default content for a notebook tab (one empty code cell)
 */
export function createDefaultNotebookContent(): NotebookTabContent {
  return {
    cells: [{
      id: crypto.randomUUID(),
      cellType: 'code' as const,
      source: '',
      executionState: 'idle' as const,
    }],
    metadata: { version: 1 },
    executionCounter: 0,
    isDirty: false,
  };
}

/**
 * Create default content for a session browser tab
 */
export function createDefaultSessionBrowserContent(): SessionBrowserTabContent {
  return {
    filter: 'active',
    autoRefreshInterval: 0,
    isLoading: true,
  };
}

/**
 * Create default content for a tablespace manager tab
 */
export function createDefaultTablespaceManagerContent(): TablespaceManagerTabContent {
  return {
    isLoading: true,
  };
}

/**
 * Create default content for an Oracle object detail tab
 */
export function createDefaultOracleObjectDetailContent(
  objectName: string,
  schema: string,
  database: string,
  objectKind: OracleDetailObjectKind,
): OracleObjectDetailTabContent {
  return {
    objectName,
    schema,
    database,
    objectKind,
    isLoading: true,
  };
}

/**
 * Create default content for a Data Pump tab
 */
export function createDefaultDataPumpContent(): DataPumpTabContent {
  return {
    mode: 'export',
    wizardStep: 'prerequisites',
    isLoading: true,
  };
}

/**
 * Create default content for an Instance Monitor tab
 */
export function createDefaultInstanceMonitorContent(): InstanceMonitorTabContent {
  return {
    activePanel: 'dashboard',
    autoRefreshInterval: 0,
    isLoading: true,
  };
}

/**
 * Create default content for a Filegroup Manager tab
 */
export function createDefaultFilegroupManagerContent(): FilegroupManagerTabContent {
  return {
    isLoading: true,
  };
}

// createDefaultPlSqlDebuggerContent is re-exported from pl-sql-debugger/types.ts above

// ============================================================================
// Tab Creation Types
// ============================================================================

/**
 * Input for creating a new query tab (content is optional, defaults will be created)
 */
export type NewQueryTabInput = {
  type: 'query';
  title: string;
  /** Set true when the title is intentional (e.g. "Schema Sync 3") so the
   *  content-derived auto-namer never renames the tab. */
  customTitle?: boolean;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<QueryTabContent>;
};

/**
 * Input for creating a new table editor tab (content is optional, defaults will be created)
 */
export type NewTableEditorTabInput = {
  type: 'table-editor';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<TableEditorTabContent>;
};

/**
 * Input for creating a new table explorer tab (content is optional, defaults will be created)
 */
export type NewTableExplorerTabInput = {
  type: 'table-explorer';
  title: string;
  /** Set true when the title is intentional (e.g. restored from a manual
   *  rename) so the content-derived auto-namer never renames the tab. */
  customTitle?: boolean;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<TableExplorerTabContent>;
};

export type NewExecutionPlanTabInput = {
  type: 'plan';
  title: string;
  /** Set true when the title is intentional (e.g. restored from a manual
   *  rename) so the content-derived auto-namer never renames the tab. */
  customTitle?: boolean;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<ExecutionPlanTabContent>;
};

/**
 * Input for creating a new table designer tab (content is optional, defaults will be created)
 */
export type NewTableDesignerTabInput = {
  type: 'table-designer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<TableDesignerTabContent>;
};

/**
 * Input for creating a new security manager tab (content is optional, defaults will be created)
 */
export type NewSecurityManagerTabInput = {
  type: 'security';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<SecurityManagerTabContent>;
};

/**
 * Input for creating a new schema compare tab (content is optional, defaults will be created)
 */
export type NewSchemaCompareTabInput = {
  type: 'schema-compare';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<SchemaCompareTabContent>;
};

/**
 * Input for creating a new database blueprint tab. `blueprintFolderId` is required —
 * a Database Blueprint tab is always tied to a mounted-folder entry.
 */
export type NewDatabaseBlueprintTabInput = {
  type: 'database-blueprint';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content: DatabaseBlueprintTabContent;
};

/**
 * Input for creating a new cross-engine migration tab. `content` is required —
 * the tab is always seeded with a concrete migration source (table or database).
 */
export type NewCrossEngineMigrateTabInput = {
  type: 'cross-engine-migrate';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content: CrossEngineMigrateTabContent;
};

/**
 * Input for creating a new data import tab (content is optional, defaults will be
 * created) — a file path is optional at open time, the wizard can open empty and
 * pick a file.
 */
export type NewDataImportTabInput = {
  type: 'data-import';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DataImportTabContent>;
};

/**
 * Input for creating a new data compare tab (content is optional, defaults will be created)
 */
export type NewDataCompareTabInput = {
  type: 'data-compare';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DataCompareTabContent>;
};

/**
 * Input for creating a new dependency viewer tab (content is optional, defaults will be created)
 */
export type NewDependencyViewerTabInput = {
  type: 'dependency-viewer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DependencyViewerTabContent>;
};

/**
 * Input for creating a new schema overview tab (content is optional, defaults will be created)
 */
export type NewSchemaOverviewTabInput = {
  type: 'schema-overview';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<SchemaOverviewTabContent>;
};

/**
 * Input for creating a new spatial explorer tab (content is optional, defaults will be created)
 */
export type NewSpatialExplorerTabInput = {
  type: 'spatial-explorer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<SpatialExplorerTabContent>;
};

/**
 * Input for creating a new data profile tab (content is optional, defaults will be created)
 */
export type NewDataProfileTabInput = {
  type: 'data-profile';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DataProfileTabContent>;
};

/**
 * Input for creating a new start page tab (content is optional, defaults will be created)
 */
export type NewStartPageTabInput = {
  type: 'start-page';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<StartPageTabContent>;
};

/**
 * Input for creating a new notebook tab (content is optional, defaults will be created)
 */
export type NewNotebookTabInput = {
  type: 'notebook';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<NotebookTabContent>;
};

/**
 * Input for creating a new session browser tab
 */
export type NewSessionBrowserTabInput = {
  type: 'session-browser';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<SessionBrowserTabContent>;
};

/**
 * Input for creating a new tablespace manager tab
 */
export type NewTablespaceManagerTabInput = {
  type: 'tablespace-manager';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<TablespaceManagerTabContent>;
};

/**
 * Input for creating a new Oracle object detail tab
 */
export type NewOracleObjectDetailTabInput = {
  type: 'oracle-object-detail';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<OracleObjectDetailTabContent>;
};

/**
 * Input for creating a new Data Pump tab
 */
export type NewDataPumpTabInput = {
  type: 'data-pump';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DataPumpTabContent>;
};

/**
 * Input for creating a new Instance Monitor tab
 */
export type NewInstanceMonitorTabInput = {
  type: 'instance-monitor';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<InstanceMonitorTabContent>;
};

/**
 * Input for creating a new Filegroup Manager tab
 */
export type NewFilegroupManagerTabInput = {
  type: 'filegroup-manager';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<FilegroupManagerTabContent>;
};

/**
 * Input for creating a new PL/SQL debugger tab
 */
export type NewPlSqlDebuggerTabInput = {
  type: 'pl-sql-debugger';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<PlSqlDebuggerTabContent>;
};

/**
 * Input for creating a new job scheduler tab
 */
export type NewJobSchedulerTabInput = {
  type: 'job-scheduler';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<JobSchedulerTabContent>;
};

/**
 * Input for creating a new PL/SQL editor tab
 */
export type NewPlSqlEditorTabInput = {
  type: 'plsql-editor';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<PlSqlEditorTabContent>;
};

/**
 * Input for creating a new package explorer tab
 */
export type NewPackageExplorerTabInput = {
  type: 'package-explorer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<PackageExplorerTabContent>;
};

/**
 * Input for creating a new dashboard tab.
 */
export type NewDashboardTabInput = {
  type: 'dashboard';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DashboardTabContent>;
};

/**
 * Input for creating a new find-references results tab. Content is required —
 * a references tab without a target identifier has nothing to render. The
 * factory `createDefaultReferencesContent` shapes the empty placeholder when
 * the consumer wants to populate the groups asynchronously.
 */
export type NewReferencesTabInput = {
  type: 'references-tab';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<ReferencesTabContent>;
};

/**
 * Union of all new tab input types.
 * Used by addTab to allow creating tabs without specifying full content.
 */
export type NewTabInput =
  | NewQueryTabInput
  | NewTableEditorTabInput
  | NewTableExplorerTabInput
  | NewExecutionPlanTabInput
  | NewTableDesignerTabInput
  | NewSecurityManagerTabInput
  | NewSchemaCompareTabInput
  | NewDataCompareTabInput
  | NewDependencyViewerTabInput
  | NewSchemaOverviewTabInput
  | NewSpatialExplorerTabInput
  | NewDataProfileTabInput
  | NewStartPageTabInput
  | NewNotebookTabInput
  | NewSessionBrowserTabInput
  | NewTablespaceManagerTabInput
  | NewOracleObjectDetailTabInput
  | NewDataPumpTabInput
  | NewInstanceMonitorTabInput
  | NewFilegroupManagerTabInput
  | NewPlSqlDebuggerTabInput
  | NewJobSchedulerTabInput
  | NewPlSqlEditorTabInput
  | NewPackageExplorerTabInput
  | NewDashboardTabInput
  | NewReferencesTabInput
  | NewDatabaseBlueprintTabInput
  | NewCrossEngineMigrateTabInput
  | NewDataImportTabInput
  | NewDeadlockAnalyzerTabInput
  | NewQueryStoreTabInput
  | NewStorageExplorerTabInput;

/** Input for creating a Storage Explorer tab — `openStorageExplorerTab` is the only intended caller. */
export type NewStorageExplorerTabInput = {
  type: 'storage-explorer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<StorageExplorerTabContent>;
};

/** Input for creating a Query Store tab — connection + database scoped. */
export type NewQueryStoreTabInput = {
  type: 'query-store';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<QueryStoreTabContent>;
};

/** Input for creating a deadlock analyzer tab — works without any connection. */
export type NewDeadlockAnalyzerTabInput = {
  type: 'deadlock-analyzer';
  title: string;
  connectionInfo?: TabConnectionInfo;
  content?: Partial<DeadlockAnalyzerTabContent>;
};

// ============================================================================
// Workspace Component Props
// ============================================================================

/**
 * Props that all workspace tab components must accept
 */
export type WorkspaceTabComponentProps = {
  tabId: string;
  className?: string;
};
